import MusicAI from "./MusicAI";
import { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut, updateProfile } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getApp } from "firebase/app";
import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, limit, updateDoc, increment, addDoc, deleteDoc, onSnapshot, where } from "firebase/firestore";
import { dedupeByArtwork, filterRelevant } from "./utils";
import Login from "./Login";
import Signup from "./Signup";
import LandingPage from "./LandingPage";
import Onboarding from "./Onboarding";
import YouTubePlayer from "./YouTubePlayer";
import MusicFeed from "./MusicFeed";
import Charts from "./Charts";
import ArtistProfile from "./ArtistProfile";
import Settings from "./Settings";
import Premium from "./Premium";
import UMSApplication from "./UMSApplication";
import UserProfile from "./UserProfile";
import Notifications from "./Notifications";
import Karaoke from "./Karaoke";
import CreateTab from "./CreateTab";
import MyMusicLogo from "./MyMusicLogo";
import { getTranslation } from "./translations";
import { initPushNotifications } from "./pushNotifications";
import { ytSearch, getItunesPreview, ytSearchMusic } from "./ytKeys";
import AdBanner from "./AdBanner";
import { HomeSkeletons, SongRowSkeleton } from "./Skeleton";

// ── NEW FROM ARTISTS ──────────────────────────────────────────────────────────
function NewFromArtists({ onPlaySong, currentSong, onSeeAll }) {
  const [songs, setSongs] = useState([]);
  useEffect(() => {
    async function load() {
      try {
      const q = query(collection(db, "songs"), where("artistVerified", "==", true), orderBy("createdAt", "desc"), limit(10));
        const snap = await getDocs(q);
        setSongs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {}
    }
    load();
  }, []);
  if (songs.length === 0) return null;
  return (
    <div style={{ padding: "20px 16px 0" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>New from Artists</span>
        <span onClick={onSeeAll} style={{ fontSize: 12, color: "#1db954", cursor: "pointer" }}>See all</span>
      </div>
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
        {songs.map(s => (
          <div key={s.id} onClick={() => onPlaySong({ trackId: s.id, trackName: s.title, artistName: s.artistName, artworkUrl100: s.coverUrl || "", previewUrl: "", isOriginal: true, audioUrl: s.audioUrl }, songs.map(x => ({ trackId: x.id, trackName: x.title, artistName: x.artistName, artworkUrl100: x.coverUrl || "", previewUrl: "", isOriginal: true, audioUrl: x.audioUrl })))}
            style={{ flexShrink: 0, width: 120, cursor: "pointer" }}>
            <div style={{ width: 120, height: 120, borderRadius: 10, background: s.coverUrl ? "transparent" : "linear-gradient(135deg,#1db954,#0a8f3c)", overflow: "hidden", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", border: currentSong?.trackId === s.id ? "2px solid #1db954" : "none" }}>
              {s.coverUrl ? <img src={s.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 40 }}>🎵</span>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.artistName}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SONG ROW ──────────────────────────────────────────────────────────────────
function SongRow({ song, index, queue, onPlaySong, currentSong, likedSongs, onToggleLike, onOpenArtist, onOpenAlbum, uid, playlists, onAddToPlaylist }) {
  const [showOptions, setShowOptions] = useState(false);
  const isLiked = likedSongs?.some(s => s.trackId === song.trackId);
  const isPlaying = currentSong?.trackId === song.trackId;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: isPlaying ? "rgba(29,185,84,0.06)" : "transparent", borderLeft: isPlaying ? "3px solid #1db954" : "3px solid transparent" }}>
      <div style={{ width: 20, textAlign: "center", color: "#555", fontSize: 12, flexShrink: 0 }}>
        {isPlaying ? (
          <div style={{ display: "flex", gap: 2, alignItems: "flex-end", justifyContent: "center", height: 14 }}>
            {[6,10,8].map((h,i) => <div key={i} style={{ width: 3, height: h, background: "#1db954", borderRadius: 1 }} />)}
          </div>
        ) : index + 1}
      </div>
      <img src={song.artworkUrl100} alt="" style={{ width: 46, height: 46, borderRadius: 8, objectFit: "cover", flexShrink: 0, cursor: "pointer" }} onClick={() => onPlaySong(song, queue)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div onClick={() => onPlaySong(song, queue)} style={{ fontSize: 14, fontWeight: isPlaying ? 700 : 500, color: isPlaying ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}>{song.trackName}</div>
        <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
          <span onClick={() => onOpenArtist(song)} style={{ fontSize: 12, color: "#888", cursor: "pointer" }}>{song.artistName}</span>
          {song.collectionName && <><span style={{ fontSize: 12, color: "#333" }}>·</span><span onClick={() => onOpenAlbum(song)} style={{ fontSize: 12, color: "#888", cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 }}>{song.collectionName}</span></>}
        </div>
      </div>
      <button onClick={() => onToggleLike(song)} style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, color: isLiked ? "#e91429" : "#555", fontSize: 18 }}>
        {isLiked ? "♥" : "♡"}
      </button>
      <button onClick={() => setShowOptions(!showOptions)} style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, color: "#555", fontSize: 20 }}>⋮</button>

      {showOptions && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => setShowOptions(false)}>
          <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", border: "1px solid #2a2a2a" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <img src={song.artworkUrl100} alt="" style={{ width: 48, height: 48, borderRadius: 8 }} />
              <div><div style={{ fontSize: 14, fontWeight: 600 }}>{song.trackName}</div><div style={{ fontSize: 12, color: "#888" }}>{song.artistName}</div></div>
            </div>
            {[
              { icon: "▶", label: "Play", action: () => { onPlaySong(song, queue); setShowOptions(false); } },
              { icon: isLiked ? "♥" : "♡", label: isLiked ? "Unlike" : "Like", action: () => { onToggleLike(song); setShowOptions(false); } },
             { icon: "👤", label: "View artist", action: () => { onOpenArtist(song); setShowOptions(false); } },
              { icon: "➕", label: "Add to playlist", action: () => { onAddToPlaylist && onAddToPlaylist(song); setShowOptions(false); } },
            ].map(item => (
              <div key={item.label} onClick={item.action} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 0", borderBottom: "1px solid #1a1a1a", cursor: "pointer" }}>
                <span style={{ fontSize: 18, width: 24, textAlign: "center" }}>{item.icon}</span>
                <span style={{ fontSize: 14, color: "#fff" }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── HOME COMPONENT ────────────────────────────────────────────────────────────
function Home({ user, language, onLanguageChange, initialProfileId, initialIsUsername }) {
  const analytics = getAnalytics(getApp());
  const uid = user.uid;
  const t = getTranslation(language || "en");

  // ── STATE ─────────────────────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [homeLoading, setHomeLoading] = useState(true);
  const [page, setPage] = useState("home");
  const [backPage, setBackPage] = useState("home");
  const [searchFocused, setSearchFocused] = useState(false);

  const [currentSong, setCurrentSong] = useState(null);
  const [currentQueue, setCurrentQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [showFullPlayer, setShowFullPlayer] = useState(false);
  const [showTab, setShowTab] = useState("related");

  const [featuredSongs, setFeaturedSongs] = useState([]);
  const [trendingSongs, setTrendingSongs] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState(() => JSON.parse(localStorage.getItem(`recentlyPlayed_${uid}`) || "[]"));
  const [likedSongs, setLikedSongs] = useState([]);
  const [followedArtists, setFollowedArtists] = useState(() => JSON.parse(localStorage.getItem(`followedArtists_${uid}`) || "[]"));
  const [playlists, setPlaylists] = useState(() => JSON.parse(localStorage.getItem(`playlists_${uid}`) || "[]"));

  const [relatedSongs, setRelatedSongs] = useState([]);
  const [lyrics, setLyrics] = useState("");
  const [lyricsObj, setLyricsObj] = useState(null);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [songKey, setSongKey] = useState(null);
  const [songKeyLoading, setSongKeyLoading] = useState(false);
  const [lyricsOffset, setLyricsOffset] = useState(0);

  const [artist, setArtist] = useState(null);
  const [artistSongs, setArtistSongs] = useState([]);
  const [artistBio, setArtistBio] = useState("");
  const [album, setAlbum] = useState(null);
  const [albumSongs, setAlbumSongs] = useState([]);

  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [addToPlaylistSong, setAddToPlaylistSong] = useState(null);

  const [notifications, setNotifications] = useState(() => JSON.parse(localStorage.getItem(`notifications_${uid}`) || "[]"));
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
 const [floatingPlayerVisible, setFloatingPlayerVisible] = useState(true);
const [floatingPlayerExpanded, setFloatingPlayerExpanded] = useState(false);
const floatingTapRef = useRef(null);
const floatingPos = useRef({ x: 16, y: 80 });
const [floatingPosState, setFloatingPosState] = useState({ x: 16, y: window.innerHeight - 140 });
const isDragging = useRef(false);
const dragStart = useRef({ x: 0, y: 0 });
  const [editToast, setEditToast] = useState(false);
const [profileTab, setProfileTab] = useState(null);
const [viewingProfileId, setViewingProfileId] = useState(null);
const [highlightedSongId, setHighlightedSongId] = useState(null);
useEffect(() => {
    if (initialProfileId) {
      if (initialIsUsername) {
        // Look up user by username
        getDocs(query(collection(db, "users"), where("username", "==", initialProfileId), limit(1))).then(snap => {
          if (!snap.empty) {
            setViewingProfileId(snap.docs[0].id);
            setPage("public-profile");
          }
        });
      } else {
        setViewingProfileId(initialProfileId);
        setPage("public-profile");
      }
    }
  }, [initialProfileId]);
  const [showUMS, setShowUMS] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(null);
  const [showKaraoke, setShowKaraoke] = useState(false);
  const [showMusicAI, setShowMusicAI] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
const [adminStats, setAdminStats] = useState(null);
  // Handle hardware back button for PWA
useEffect(() => {
  const handlePopState = () => {
    if (showFullPlayer) { setShowFullPlayer(false); return; }
    if (showNotifications) { setShowNotifications(false); return; }
    if (showSettings) { setShowSettings(false); return; }
    if (showPremium) { setShowPremium(false); return; }
    if (showUMS) { setShowUMS(false); return; }
    if (showKaraoke) { setShowKaraoke(false); return; }
    if (showMusicAI) { setShowMusicAI(false); return; }
    if (page !== "home") { setPage("home"); return; }
  };
  window.addEventListener("popstate", handlePopState);
  window.history.pushState({ page }, "", window.location.href);
  return () => window.removeEventListener("popstate", handlePopState);
}, [page, showFullPlayer, showNotifications, showSettings, showPremium, showUMS, showKaraoke, showMusicAI]);
  const [userFirebaseProfile, setUserFirebaseProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [newVersion, setNewVersion] = useState(false);

  const [pullStartY, setPullStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const playerRef = useRef(null);
  const timerRef = useRef(null);

  // ── EFFECTS ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadHomeData();
    loadLikedSongs();
    initPushNotifications(uid);
    // Load user Firebase profile
getDoc(doc(db, "users", uid)).then(snap => {
  if (snap.exists()) setUserFirebaseProfile(snap.data());
});
  }, []);

  useEffect(() => {
    const go = () => setIsOnline(true);
    const stop = () => setIsOnline(false);
    window.addEventListener("online", go);
    window.addEventListener("offline", stop);
    return () => { window.removeEventListener("online", go); window.removeEventListener("offline", stop); };
  }, []);

  useEffect(() => {
    try { logEvent(analytics, "page_view", { page_title: page }); } catch {}
  }, [page]);

  useEffect(() => {
    const handler = () => setNewVersion(true);
    document.addEventListener("newVersionAvailable", handler);
    return () => document.removeEventListener("newVersionAvailable", handler);
  }, []);

  useEffect(() => {
    if (messagesEndRef?.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  });

  const messagesEndRef = useRef(null);

  async function loadHomeData() {
    setHomeLoading(true);
    try {
      const cachedFeatured = localStorage.getItem("cache_featured");
      if (cachedFeatured) {
        const { data, time } = JSON.parse(cachedFeatured);
        if (Date.now() - time < 3600000) { setFeaturedSongs(data); setHomeLoading(false); }
        else fetchFeatured();
      } else fetchFeatured();

      const cachedTrending = localStorage.getItem("cache_trending");
      if (cachedTrending) {
        const { data, time } = JSON.parse(cachedTrending);
        if (Date.now() - time < 3600000) { setTrendingSongs(data); }
        else fetchTrending();
      } else fetchTrending();
    } catch { setHomeLoading(false); }
  }

  async function fetchFeatured() {
    try {
      const queries = ["top hits 2024", "billboard hot 100", "pop hits 2024", "viral songs 2024"];
      const q = queries[Math.floor(Math.random() * queries.length)];
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(q)}&media=music&limit=30`);
      const d = await r.json();
      const results = d.results || [];

      // Deduplicate by artwork URL to avoid repeated album covers
      const seen = new Set();
      const unique = [];
      for (const song of results) {
        if (!seen.has(song.artworkUrl100) && song.artworkUrl100) {
          seen.add(song.artworkUrl100);
          unique.push(song);
        }
        if (unique.length >= 8) break;
      }

      setFeaturedSongs(unique);
      localStorage.setItem("cache_featured", JSON.stringify({ data: unique, time: Date.now() }));
    } catch {}
    setHomeLoading(false);
  }

  async function fetchTrending() {
    try {
      const r = await fetch("https://itunes.apple.com/search?term=trending+music&media=music&limit=10");
      const d = await r.json();
      setTrendingSongs(d.results || []);
      localStorage.setItem("cache_trending", JSON.stringify({ data: d.results, time: Date.now() }));
    } catch {}
  }

  async function loadLikedSongs() {
    try {
      const snap = await getDocs(collection(db, "users", uid, "likes"));
      setLikedSongs(snap.docs.map(d => ({ trackId: d.id, ...d.data() })));
    } catch {}
  }

  async function handleRefresh() {
    setRefreshing(true);
    await fetchFeatured();
    await fetchTrending();
    setRefreshing(false);
    setPullDistance(0);
  }

  // ── PLAYER ────────────────────────────────────────────────────────────────
  function startTimer() {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (playerRef.current) {
        const ct = playerRef.current.getCurrentTime() || 0;
        const dur = playerRef.current.getDuration() || 0;
        setCurrentTime(ct);
        setDuration(dur);
        if (dur > 0 && ct >= dur - 1) playNext();
      }
    }, 1000);
  }

  function fmt(secs) {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  }

  function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return t?.goodMorning || "Good morning";
    if (h < 17) return t?.goodAfternoon || "Good afternoon";
    return t?.goodEvening || "Good evening";
  }

  async function fetchAndPlay(song) {
    try { logEvent(analytics, "song_play", { song_name: song.trackName, artist: song.artistName }); } catch {}
    setFloatingPlayerVisible(true);
    setFloatingPlayerExpanded(false);
    // Track listener for leaderboard
if (song.artistId && song.artistId !== uid) {
  try {
    const listenerRef = doc(db, "artists", song.artistId, "topListeners", uid);
    const listenerDoc = await getDoc(listenerRef);
    const currentPlays = listenerDoc.exists() ? (listenerDoc.data().plays || 0) : 0;
    await setDoc(listenerRef, {
      userId: uid,
      userName: user.displayName || user.email?.split("@")[0] || "Fan",
      userPhoto: user.photoURL || "",
      plays: currentPlays + 1,
      lastListened: new Date().toISOString(),
    });
  } catch {}
}
    setCurrentSong(song); setCurrentTime(0); setDuration(0); setIsPlaying(false); setSongKey(null);
    setRelatedSongs([]);
    fetchRelated(song); fetchLyrics(song); fetchSongKey(song); setShowTab("related");
    setRecentlyPlayed(prev => {
      const filtered = prev.filter(s => s.trackId !== song.trackId);
      const updated = [song, ...filtered].slice(0, 20);
      localStorage.setItem(`recentlyPlayed_${uid}`, JSON.stringify(updated));
      return updated;
    });
    try {
      const cacheKey = `yt_${song.trackId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setVideoId(cached);
      } else if (song.isOriginal && song.audioUrl) {
        setVideoId(null);
      } else {
        const queries = [
          `${song.trackName} ${song.artistName} official audio`,
          `${song.trackName} ${song.artistName} lyrics`,
          `${song.trackName} ${song.artistName}`,
          song.trackName,
        ];
        let found = false;
        for (const q of queries) {
          const items = await ytSearch(q, 5);
          if (items.length > 0) {
            const vid = items[0].id?.videoId;
            if (vid) { setVideoId(vid); localStorage.setItem(cacheKey, vid); found = true; break; }
          }
        }
        if (!found) {
          const preview = await getItunesPreview(song.trackName, song.artistName);
          if (preview) { setVideoId(null); const a = new Audio(preview); a.play(); setIsPlaying(true); }
        }
      }
    } catch {}

    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: song.trackName, artist: song.artistName, album: song.collectionName || "MyMusic",
        artwork: [{ src: song.artworkUrl100?.replace("100x100", "300x300") || "/logo192.png", sizes: "300x300", type: "image/jpeg" }]
      });
      navigator.mediaSession.setActionHandler("play", () => { playerRef.current?.playVideo(); setIsPlaying(true); startTimer(); });
      navigator.mediaSession.setActionHandler("pause", () => { playerRef.current?.pauseVideo(); setIsPlaying(false); });
      navigator.mediaSession.setActionHandler("nexttrack", () => playNext());
      navigator.mediaSession.setActionHandler("previoustrack", () => playPrev());
      navigator.mediaSession.playbackState = "playing";
    }
  }

  async function playSong(song, queue) {
    const q = queue || currentQueue;
    const idx = q.findIndex(s => s.trackId === song.trackId);
    setCurrentQueue(q); setCurrentIndex(idx >= 0 ? idx : 0);
    await fetchAndPlay(song);
  }

 async function playNext() {
    if (currentQueue.length === 0) return;
    if (repeat) { playerRef.current?.seekTo(0, true); playerRef.current?.playVideo(); return; }
    
    const isLastSong = !shuffle && currentIndex >= currentQueue.length - 1;
    
    if (isLastSong && currentSong) {
      // Radio mode — fetch related songs and continue playing
      try {
        const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(currentSong.artistName)}&media=music&limit=10`);
        const d = await r.json();
        const related = dedupeByArtwork(d.results || []).filter(s => s.trackId !== currentSong.trackId);
        if (related.length > 0) {
          setCurrentQueue(prev => [...prev, ...related]);
          setCurrentIndex(currentQueue.length);
          fetchAndPlay(related[0]);
          return;
        }
      } catch {}
    }
    
    const next = shuffle ? Math.floor(Math.random() * currentQueue.length) : (currentIndex + 1) % currentQueue.length;
    setCurrentIndex(next); fetchAndPlay(currentQueue[next]);
  }

  function playPrev() {
    if (currentQueue.length === 0) return;
    const prev = shuffle ? Math.floor(Math.random() * currentQueue.length) : (currentIndex - 1 + currentQueue.length) % currentQueue.length;
    setCurrentIndex(prev); fetchAndPlay(currentQueue[prev]);
  }

  function togglePlay() {
    if (isPlaying) { playerRef.current?.pauseVideo(); setIsPlaying(false); clearInterval(timerRef.current); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; }
    else { playerRef.current?.playVideo(); setIsPlaying(true); startTimer(); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; }
  }

  function stopSong() {
    try { playerRef.current?.stopVideo(); } catch {}
    setCurrentSong(null); setVideoId(null); setIsPlaying(false); setCurrentTime(0); setDuration(0);
    setShowFullPlayer(false); clearInterval(timerRef.current);
  }

  // ── LYRICS / RELATED / KEY ─────────────────────────────────────────────────
async function fetchLyrics(song) {
    setLyrics(""); setLyricsLoading(true);
    try {
      const r = await fetch(`https://api.lyrics.ovh/v1/${encodeURIComponent(song.artistName)}/${encodeURIComponent(song.trackName)}`);
      const d = await r.json();
      const lyricsText = d.lyrics || "Lyrics not found.";
      setLyrics(lyricsText);
      // Also set structured lyrics for Karaoke
      setLyricsObj({
        type: "plain",
        text: lyricsText,
        lines: lyricsText.split("\n").filter(l => l.trim()).map((line, i) => ({
          text: line,
          startTime: i * 3,
          endTime: (i + 1) * 3
        }))
      });
    } catch { 
      setLyrics("Lyrics not available.");
      setLyricsObj(null);
    }
    setLyricsLoading(false);
  }

  async function fetchRelated(song) {
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(song.artistName)}&media=music&limit=20`);
      const d = await r.json();
      const filtered = d.results.filter(s => s.trackId !== song.trackId);
      setRelatedSongs(filtered);
      setCurrentQueue(prev => { const newSongs = filtered.filter(s => !prev.find(q => q.trackId === s.trackId)); return [...prev, ...newSongs]; });
    } catch {}
  }

 async function fetchSongKey(song) {
  setSongKey(null); setSongKeyLoading(true);
  try {
    // First get real lyrics
    let realLyrics = "";
    try {
      const lyricsRes = await fetch(`https://api.lyrics.ovh/v1/${encodeURIComponent(song.artistName)}/${encodeURIComponent(song.trackName)}`);
      const lyricsData = await lyricsRes.json();
      realLyrics = lyricsData.lyrics || "";
    } catch {}

    const prompt = realLyrics
? `You are a professional music transcriber. Here are the verified lyrics for "${song.trackName}" by "${song.artistName}":

${realLyrics.slice(0, 2000)}

Create a concise chord sheet using these EXACT lyrics. Use the ACTUAL recorded key.

Format EXACTLY like this - nothing more:

${song.trackName.toUpperCase()} - ${song.artistName.toUpperCase()}
Key: [real key] | Capo: [capo] | BPM: [real bpm]

[Verse 1]
G              D
[exact lyric line]
Em             C
[exact lyric line]

[Chorus]
G    D    Em   C
[exact lyric line]

[continue all sections]

GUITAR:
G: 320003 | D: xx0232 | Em: 022000 | C: x32010

PIANO:
G: G-B-D | D: D-F#-A | Em: E-G-B | C: C-E-G

STRICT RULES - FOLLOW EXACTLY:
1. No markdown tables
2. No strumming patterns  
3. No performance notes
4. No explanations or tips
5. No asterisks or bold text
6. Show lyrics with chords above them
7. Keep it clean like a simple chord sheet
8. Only sections: song title, key info, [Section] labels, chords+lyrics, GUITAR:, PIANO:
9. Nothing else`
      : `Create a short chord sheet for "${song.trackName}" by "${song.artistName}". Include real key, BPM, capo, chord progressions for each section, guitar chord shapes, piano notes. No tables, no strumming patterns, no performance notes. Keep it concise.`;
     
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer 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",
        "HTTP-Referer": "https://mymusicstream.online",
        "X-Title": "MyMusic"
      },
      body: JSON.stringify({
model: "openai/gpt-oss-120b:free",
        max_tokens: 1500,
        messages: [{ role: "user", content: prompt }]
      })
    });
    const data = await response.json();
    const result = data.choices?.[0]?.message?.content;
    if (result) {
      setSongKey(result);
    } else {
      setSongKey("Could not generate chord sheet for this song.");
    }
  } catch {
    setSongKey("Could not analyze this song. Try again.");
  }
  setSongKeyLoading(false);
}

  // ── SEARCH ────────────────────────────────────────────────────────────────

  async function searchSongs(overrideQuery) {
    const searchTerm = overrideQuery !== undefined ? overrideQuery : query;
    if (!searchTerm.trim()) return;
    try { logEvent(analytics, "search", { search_term: searchTerm }); } catch {}
    setLoading(true); setSongs([]); setPage("search");

    // Track search history for music personality
    const searches = JSON.parse(localStorage.getItem(`searches_${uid}`) || "[]");
    searches.unshift({ query: searchTerm.trim(), time: Date.now() });
    localStorage.setItem(`searches_${uid}`, JSON.stringify(searches.slice(0, 100)));

    const cacheKey = searchTerm.trim().toLowerCase();

    // 1. Try Firestore cache first
    try {
      const cacheDoc = await getDoc(doc(db, "songsCache", cacheKey));
      if (cacheDoc.exists()) {
        const cached = cacheDoc.data().results || [];
        if (cached.length >= 10) {
          setSongs(cached);
          setLoading(false);
          return;
        }
      }
    } catch {}

   // 2. Fall back to iTunes API
    let itunesResults = [];
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(searchTerm)}&media=music&limit=30`);
      const d = await r.json();
      itunesResults = dedupeByArtwork(filterRelevant(d.results || [], searchTerm));
    } catch {}

    // 3. If iTunes gives weak results, fall back to YouTube Music search (handles Burmese/non-English better)
    let finalResults = itunesResults;
    if (itunesResults.length < 5) {
      try {
        const ytResults = await ytSearchMusic(searchTerm, 20);
        // Merge YouTube results that aren't already covered, avoiding duplicates by title similarity
        const combined = [...itunesResults, ...ytResults];
        finalResults = dedupeByArtwork(combined);
      } catch {}
    }

    setSongs(finalResults);

    // 4. Save to cache for next time
    if (finalResults.length > 0) {
      try {
        await setDoc(doc(db, "songsCache", cacheKey), {
          results: finalResults,
          query: searchTerm.trim(),
          cachedAt: new Date().toISOString()
        });
      } catch {}
    }
    setLoading(false);
  }
  

  async function searchSuggestions(q) {
    if (!q.trim()) { setSuggestions([]); return; }
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(q)}&media=music&limit=5`);
      const d = await r.json();
      setSuggestions(d.results?.map(s => s.trackName) || []);
    } catch {}
  }

  // ── ARTIST / ALBUM ────────────────────────────────────────────────────────
 async function openArtist(song) {
    // Check if this artist has a MyMusic account
    try {
      const artistSnap = await getDocs(query(collection(db, "artists"), where("displayName", "==", song.artistName), limit(1)));
      if (!artistSnap.empty) {
        const artistId = artistSnap.docs[0].id;
        setViewingProfileId(artistId);
        setPage("public-profile");
        return;
      }
    } catch {}

    // Fall back to iTunes artist page
    setArtist(song); setPage("artist"); setLoading(true); setBackPage("search");
    fetchArtistBio(song.artistName);
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(song.artistName)}&media=music&limit=20`);
      const d = await r.json();
      setArtistSongs(dedupeByArtwork(d.results || []));
    } catch {}
    setLoading(false);
  }

  async function fetchArtistBio(name) {
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(name)}&media=music&limit=1`);
      const d = await r.json();
      setArtistBio(d.results?.[0] ? `${name} is an artist with ${d.results.length}+ tracks available on MyMusic.` : "");
    } catch {}
  }

  async function openAlbum(song) {
    setAlbum(song); setPage("album"); setLoading(true); setBackPage("search");
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(song.collectionName)}&media=music&limit=20`);
      const d = await r.json();
      setAlbumSongs(d.results || []);
    } catch {}
    setLoading(false);
  }

  // ── LIKES ─────────────────────────────────────────────────────────────────
  async function toggleLike(song) {
    const likeRef = doc(db, "users", uid, "likes", song.trackId?.toString());
    if (likedSongs.some(s => s.trackId === song.trackId)) {
      await deleteDoc(likeRef);
      setLikedSongs(prev => prev.filter(s => s.trackId !== song.trackId));
    } else {
      await setDoc(likeRef, { ...song, timestamp: new Date().toISOString() });
      setLikedSongs(prev => [...prev, song]);
    }
  }

  // ── PLAYLISTS ─────────────────────────────────────────────────────────────
  function createPlaylist() {
    if (!newPlaylistName.trim()) return;
    const pl = { id: Date.now(), name: newPlaylistName.trim(), songs: [] };
    const updated = [...playlists, pl];
    setPlaylists(updated);
    localStorage.setItem(`playlists_${uid}`, JSON.stringify(updated));
    setNewPlaylistName(""); setShowPlaylistModal(false);
  }

  function addToPlaylist(playlistId, song) {
    const updated = playlists.map(p => {
      if (p.id !== playlistId) return p;
      if (p.songs.find(s => s.trackId === song.trackId)) return p;
      return { ...p, songs: [...p.songs, song] };
    });
    setPlaylists(updated);
    localStorage.setItem(`playlists_${uid}`, JSON.stringify(updated));
    setShowAddToPlaylist(null);
  }

  function removeFromPlaylist(playlistId, trackId) {
    const updated = playlists.map(p => p.id === playlistId ? { ...p, songs: p.songs.filter(s => s.trackId !== trackId) } : p);
    setPlaylists(updated);
    localStorage.setItem(`playlists_${uid}`, JSON.stringify(updated));
  }

  function deletePlaylist(id) {
    const updated = playlists.filter(p => p.id !== id);
    setPlaylists(updated);
    localStorage.setItem(`playlists_${uid}`, JSON.stringify(updated));
    setSelectedPlaylist(null); setPage("library");
  }

  // ── STYLES ────────────────────────────────────────────────────────────────
  const S = {
   app: { minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 120, overflowX: "hidden", overflowY: "auto" },
    topbar: { background: "#000", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0, zIndex: 100, borderBottom: "1px solid #0a0a0a" },
    bottomnav: { position: "fixed", bottom: 0, left: 0, right: 0, height: 60, background: "rgba(0,0,0,0.97)", borderTop: "1px solid #1a1a1a", display: "flex", alignItems: "center", zIndex: 501, backdropFilter: "blur(20px)" },
    playerbar: { position: "fixed", bottom: 60, left: 0, right: 0, background: "rgba(18,18,18,0.98)", borderTop: "1px solid #1a1a1a", padding: "10px 14px", zIndex: 500, backdropFilter: "blur(20px)" },
    searchWrap: { flex: 1, position: "relative" },
    searchInput: { width: "100%", height: 40, padding: "0 12px 0 36px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#1a1a1a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none", boxSizing: "border-box" },
    backBtn: { background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  };

  const userProfile = null;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div id="main-scroll" style={S.app}
      onTouchStart={e => { if (window.scrollY === 0) setPullStartY(e.touches[0].clientY); }}
      onTouchMove={e => { if (pullStartY === 0) return; const dist = e.touches[0].clientY - pullStartY; if (dist > 0 && dist < 100) { setPullDistance(dist); setIsPulling(true); } }}
      onTouchEnd={() => { if (pullDistance > 60) handleRefresh(); else setPullDistance(0); setIsPulling(false); setPullStartY(0); }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pop { 0%{transform:scale(1)} 50%{transform:scale(0.92)} 100%{transform:scale(1)} }
@keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
button:active { transform: scale(0.95); transition: transform 0.1s; }
        @keyframes eq1 { from{height:6px} to{height:14px} }
        @keyframes eq2 { from{height:10px} to{height:6px} }
        @keyframes eq3 { from{height:14px} to{height:10px} }
        input[type=range] { accent-color: #1db954; }
        ::-webkit-scrollbar { width: 3px; height: 3px; }
        ::-webkit-scrollbar-thumb { background: #2a2a2a; border-radius: 3px; }
      `}</style>

      {videoId && (
        <YouTubePlayer videoId={videoId}
          onReady={p => { playerRef.current = p; p.setVolume(volume); setIsPlaying(true); startTimer(); }}
          onStateChange={state => { if (state === 0) playNext(); }}
        />
      )}

      {/* OVERLAYS */}
      {showNotifications && <Notifications user={user} notifications={notifications} setNotifications={setNotifications} onBack={() => setShowNotifications(false)} uid={uid} onNavigate={(type, id) => {
  setShowNotifications(false);
  if (type === "feed") {
    setPage("feed");
    setHighlightedSongId(id);
  } else if (type === "profile") {
    setViewingProfileId(id);
    setPage("public-profile");
  }
}} />}
      {showSettings && <Settings user={user} onBack={() => setShowSettings(false)} onLanguageChange={onLanguageChange} language={language} onSignOut={() => signOut(auth)} onShowPremium={() => setShowPremium(true)} />}
      {showPremium && <Premium user={user} onBack={() => setShowPremium(false)} />}
     {/* Profile Updated Toast */}
{editToast && (
  <div style={{ position: "fixed", top: 80, left: "50%", transform: "translateX(-50%)", background: "#1db954", color: "#000", padding: "10px 20px", borderRadius: 50, fontSize: 13, fontWeight: 700, zIndex: 999, whiteSpace: "nowrap" }}>
    ✅ Profile updated!
  </div>
)}
      {/* Edit Profile Modal */}
{showEditProfile && (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 400, display: "flex", alignItems: "flex-end" }} onClick={() => setShowEditProfile(false)}>
   <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", height: "85vh", overflowY: "auto", border: "1px solid #2a2a2a" }} onClick={e => e.stopPropagation()}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Edit Profile</div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 24 }}>Update your profile information</div>

      {/* Profile Photo */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#111", overflow: "hidden", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #333" }}>
          {user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 36, height: 36, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
        </div>
      <label style={{ cursor: "pointer" }}>
  <div style={{ background: "none", border: "1px solid #333", color: "#fff", padding: "6px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer", display: "inline-block" }}>Change Photo</div>
  <input type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "mymusic_unsigned");
    formData.append("cloud_name", "dsczkplah");
    try {
      const r = await fetch("https://api.cloudinary.com/v1_1/dsczkplah/image/upload", { method: "POST", body: formData });
      const d = await r.json();
      await updateProfile(auth.currentUser, { photoURL: d.secure_url });
      await setDoc(doc(db, "users", user.uid), { photoURL: d.secure_url }, { merge: true });
      alert("Photo updated!");
    } catch { alert("Failed to upload photo."); }
  }} />
</label>
      </div>

      {/* Display Name */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Display Name</div>
        <input
          id="edit-name"
          defaultValue={user.displayName || ""}
          placeholder="Your name..."
          style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {/* Bio */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Bio</div>
        <textarea
          id="edit-bio"
          defaultValue={userFirebaseProfile?.bio || ""}
          placeholder="Tell the world about yourself..."
          maxLength={150}
          style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", resize: "none", height: 90, boxSizing: "border-box" }}
        />
        <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>Max 150 characters</div>
      </div>

      <button onClick={async () => {
        const newName = document.getElementById("edit-name").value.trim();
        const newBio = document.getElementById("edit-bio").value.trim();
        try {
          await updateProfile(auth.currentUser, { displayName: newName || user.displayName });
          await setDoc(doc(db, "users", user.uid), { bio: newBio, displayName: newName || user.displayName }, { merge: true });
          // Refresh user profile from Firestore
          const updatedDoc = await getDoc(doc(db, "users", user.uid));
          if (updatedDoc.exists()) setUserFirebaseProfile(updatedDoc.data());
          setShowEditProfile(false);
         setEditToast(true); setTimeout(() => setEditToast(false), 3000);
        } catch (e) {
          alert("Failed to update. Try again.");
        }
      }} style={{ width: "100%", padding: 14, borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
        Save Changes
      </button>
    </div>
  </div>
)}
      {showUMS && <UMSApplication user={user} onBack={() => setShowUMS(false)} isAdmin={user.email === "lawhaling91069@gmail.com"} />}
      {showUserProfile && <UserProfile userId={showUserProfile} currentUser={user} onBack={() => setShowUserProfile(null)}onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} />}
     {showKaraoke && currentSong && <Karaoke song={currentSong} onClose={() => setShowKaraoke(false)} lyrics={lyricsObj} currentTime={currentTime} playerRef={playerRef} setVideoId={setVideoId} />}
        {showMusicAI && <MusicAI song={currentSong} onClose={() => setShowMusicAI(false)} />}
          {showAdminDashboard && user.email === "lawhaling91069@gmail.com" && (
  <AdminDashboard onClose={() => setShowAdminDashboard(false)} db={db} />
)}

      {/* BANNERS */}
      {newVersion && (
        <div style={{ background: "#fff", color: "#000", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13, position: "sticky", top: 0, zIndex: 201 }}>
          <span>🆕 New version available</span>
          <button onClick={() => window.location.reload()} style={{ background: "#000", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Update</button>
        </div>
      )}
      {!isOnline && (
        <div style={{ background: "#e91429", color: "#fff", padding: "8px 16px", textAlign: "center", fontSize: 12, fontWeight: 600, position: "sticky", top: 0, zIndex: 201 }}>
          📡 No internet connection
        </div>
      )}
      {(isPulling || refreshing) && (
        <div style={{ position: "fixed", top: 70, left: 0, right: 0, display: "flex", justifyContent: "center", zIndex: 150, pointerEvents: "none" }}>
          <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 50, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            {refreshing ? <><div style={{ width: 14, height: 14, border: "2px solid #222", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><span style={{ fontSize: 12, color: "#fff" }}>Refreshing...</span></> : <span style={{ fontSize: 12, color: pullDistance > 60 ? "#fff" : "#555" }}>{pullDistance > 60 ? "Release to refresh" : "Pull to refresh"}</span>}
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <div style={S.topbar}>
  {["artist","album","playlist","liked","terms","privacy","about"].includes(page) ? (
    <button style={S.backBtn} onClick={() => setPage(backPage)}>
      <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
    </button>
  ) : (
    <>
      {!searchFocused && <MyMusicLogo size={28} showText={true} />}
      <div style={{ flex: 1, position: "relative" }}>
        <svg viewBox="0 0 24 24" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, fill: "none", stroke: "#555", strokeWidth: 2, pointerEvents: "none" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input value={query} onChange={e => { setQuery(e.target.value); searchSuggestions(e.target.value); }}
          placeholder={t?.search || "Search..."}
          style={{ width: "100%", height: 38, padding: "0 12px 0 34px", borderRadius: 50, border: "1px solid #1a1a1a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none", boxSizing: "border-box" }}
          onFocus={() => setSearchFocused(true)}
         onBlur={() => { setTimeout(() => { setSuggestions([]); setSearchFocused(false); }, 150); }}
          onKeyDown={e => { if (e.key === "Enter") { searchSongs(); setSuggestions([]); } if (e.key === "Escape") { setSuggestions([]); setSearchFocused(false); } }}
        />
        {suggestions.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#111", borderRadius: 14, padding: "6px 0", zIndex: 50, border: "1px solid #222", marginTop: 6, boxShadow: "0 8px 32px rgba(0,0,0,0.6)", overflow: "hidden" }}>
            {suggestions.map((s, i) => (
              <div key={i} onMouseDown={() => { setQuery(s); setSuggestions([]); searchSongs(); }}
                style={{ padding: "10px 16px", fontSize: 13, cursor: "pointer", color: "#ccc", display: "flex", alignItems: "center", gap: 10, transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#555", strokeWidth: 2, flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
      {!searchFocused && (
        <button onClick={() => setShowNotifications(true)} style={{ background: "none", border: "none", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#fff", strokeWidth: 1.8 }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          {notifications.filter(n => !n.read).length > 0 && <div style={{ position: "absolute", top: 0, right: 0, width: 8, height: 8, background: "#e91429", borderRadius: "50%", border: "1.5px solid #000" }} />}
        </button>
      )}
    </>
  )}
</div>

        

      {/* ── HOME PAGE ── */}
      {page === "home" && (
        <div>
          <div style={{ padding: "20px 16px 16px", background: "linear-gradient(160deg,#0d2818 0%,#000 65%)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1db954", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 4 }}>{getGreeting()}</div>
            <div style={{ fontSize: 13, color: "#777" }}>Welcome back, <span style={{ color: "#fff", fontWeight: 500 }}>{user.displayName || user.email?.split("@")[0]}</span></div>
          </div>

          {homeLoading && <HomeSkeletons />}

          <NewFromArtists onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} onSeeAll={() => setPage("feed")} />

          {recentlyPlayed.length > 0 && (
            <div style={{ padding: "20px 16px 0" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
                <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>{t?.recentlyPlayed || "Recently Played"}</span>
                <span style={{ fontSize: 12, color: "#1db954", cursor: "pointer" }}>{t?.seeAll || "See all"}</span>
              </div>
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                {recentlyPlayed.slice(0, 10).map(s => (
                  <div key={s.trackId} onClick={() => { playSong(s, recentlyPlayed); setShowFullPlayer(true); }} style={{ flexShrink: 0, width: 120, cursor: "pointer" }}>
                    <div style={{ position: "relative", marginBottom: 8 }}>
                      <img src={s.artworkUrl100} alt="" style={{ width: 120, height: 120, borderRadius: 10, display: "block", objectFit: "cover" }} />
                      {currentSong?.trackId === s.trackId && <div style={{ position: "absolute", bottom: 6, right: 6, width: 28, height: 28, borderRadius: "50%", background: "#1db954", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 12 }}>{[6,10,8].map((h,i) => <div key={i} style={{ width: 3, height: h, background: "#000", borderRadius: 1, animation: `eq${i+1} 0.8s ease infinite alternate` }} />)}</div></div>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.trackName}</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.artistName}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {featuredSongs.length > 0 && (
            <div style={{ padding: "20px 16px 0" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800, marginBottom: 14 }}>{t?.topHits || "Top Hits"}</div>
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                {featuredSongs.map(s => (
                  <div key={s.trackId} onClick={() => { playSong(s, featuredSongs); setShowFullPlayer(true); }} style={{ flexShrink: 0, width: 150, cursor: "pointer" }}>
                    <div style={{ position: "relative", marginBottom: 8 }}>
                      <img src={s.artworkUrl100} alt="" style={{ width: 150, height: 150, borderRadius: 10, display: "block", objectFit: "cover" }} />
                      <div style={{ position: "absolute", bottom: 8, right: 8, width: 36, height: 36, borderRadius: "50%", background: currentSong?.trackId === s.trackId ? "#1db954" : "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {currentSong?.trackId === s.trackId && isPlaying ? <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#000" }}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> : <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#fff", marginLeft: 2 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.trackName}</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{s.artistName}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Browse genres */}
          <div style={{ padding: "20px 16px 0" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800, marginBottom: 14 }}>{t?.browse || "Browse"}</div>
            {/* Trending & Hot sections */}
<div style={{ padding: "20px 16px 0" }}>
  {/* Hot Right Now */}
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <div style={{ width: 3, height: 18, background: "#e91429", borderRadius: 2 }} />
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>🔥 Hot Right Now</div>
    </div>
    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
      {["Taylor Swift", "Billie Eilish", "The Weeknd", "BTS", "Lay Phyu", "Coldplay", "Drake", "SZA"].map((artist, i) => (
        <div key={i} onMouseDown={() => { setQuery(artist); searchSongs(artist); }}
          style={{ flexShrink: 0, padding: "8px 16px", borderRadius: 50, background: "#111", border: "1px solid #222", fontSize: 13, color: "#ccc", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#1db954"; e.currentTarget.style.color = "#000"; e.currentTarget.style.border = "1px solid #1db954"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#111"; e.currentTarget.style.color = "#ccc"; e.currentTarget.style.border = "1px solid #222"; }}>
          {artist}
        </div>
      ))}
    </div>
  </div>

  {/* Trending in Myanmar */}
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <div style={{ width: 3, height: 18, background: "#f59e0b", borderRadius: 2 }} />
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>🇲🇲 Trending in Myanmar</div>
    </div>
    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
      {["Lay Phyu", "Htoi Htoi", "Sai Sai", "Phyu Phyu", "Myanmar Songs", "Chit Thu Wai", "Iron Cross", "Big Bag"].map((artist, i) => (
        <div key={i} onMouseDown={() => { setQuery(artist); searchSongs(artist); }}
          style={{ flexShrink: 0, padding: "8px 16px", borderRadius: 50, background: "#111", border: "1px solid #2a1a00", fontSize: 13, color: "#f59e0b", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#f59e0b"; e.currentTarget.style.color = "#000"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#111"; e.currentTarget.style.color = "#f59e0b"; }}>
          {artist}
        </div>
      ))}
    </div>
  </div>

  {/* Trending in K-Pop */}
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <div style={{ width: 3, height: 18, background: "#8b5cf6", borderRadius: 2 }} />
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>🇰🇷 Trending K-Pop</div>
    </div>
    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
      {["BTS", "BLACKPINK", "TWICE", "Stray Kids", "NewJeans", "aespa", "IVE", "EXO"].map((artist, i) => (
        <div key={i} onMouseDown={() => {setQuery(artist); searchSongs(artist); }}
          style={{ flexShrink: 0, padding: "8px 16px", borderRadius: 50, background: "#111", border: "1px solid #1a0d2e", fontSize: 13, color: "#8b5cf6", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#8b5cf6"; e.currentTarget.style.color = "#000"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#111"; e.currentTarget.style.color = "#8b5cf6"; }}>
          {artist}
        </div>
      ))}
    </div>
  </div>
</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { name: t?.topHits || "Top Hits", emoji: "🔥", color: "#e13300", q: "top hits 2024" },
                { name: "Hip Hop", emoji: "🎤", color: "#8d67ab", q: "hip hop hits" },
                { name: "Pop", emoji: "🎵", color: "#1e3264", q: "pop hits 2024" },
                { name: "R&B", emoji: "💜", color: "#503750", q: "rnb soul hits" },
                { name: "Rock", emoji: "🎸", color: "#ba5d07", q: "rock hits" },
                { name: "K-Pop", emoji: "🇰🇷", color: "#148a08", q: "kpop hits 2024" },
                { name: "Myanmar", emoji: "🇲🇲", color: "#c6951a", q: "myanmar music 2024" },
                { name: "Christian", emoji: "✝️", color: "#0d3564", q: "christian music 2024" },
              ].map(cat => (
                <div key={cat.name} onClick={() => { setQuery(cat.q); searchSongs(cat.q); }}
                  style={{ background: cat.color, borderRadius: 10, padding: "16px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 60, position: "relative", overflow: "hidden" }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{cat.name}</span>
                  <span style={{ fontSize: 28, transform: "rotate(15deg)" }}>{cat.emoji}</span>
                </div>
              ))}
            </div>
          </div>

          {!userFirebaseProfile?.premium && <AdBanner />}

          {trendingSongs.length > 0 && (
            <div style={{ padding: "20px 16px 0" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800, marginBottom: 14 }}>Trending</div>
              {trendingSongs.map((s, i) => (
                <SongRow key={s.trackId} song={s} index={i} queue={trendingSongs} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />
              ))}
            </div>
          )}



          

          <div style={{ height: 20 }} />
        </div>
      )}

      {/* ── SEARCH PAGE ── */}
      {page === "search" && (
        <div style={{ padding: "16px 0 0" }}>
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>
              {loading ? "Searching..." : songs.length > 0 ? `Results for "${query}"` : query ? `No results for "${query}"` : "Browse"}
            </div>
          </div>
          {loading && [1,2,3,4,5].map(i => <SongRowSkeleton key={i} />)}
          {!loading && songs.length > 0 && songs.map((s, i) => (
            <SongRow key={s.trackId} song={s} index={i} queue={songs} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />
          ))}
          {!loading && songs.length === 0 && (
            <div style={{ padding: "0 16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { name: "Top Hits", emoji: "🔥", color: "#e13300", q: "top hits 2024" },
                  { name: "Hip Hop", emoji: "🎤", color: "#8d67ab", q: "hip hop hits" },
                  { name: "Pop", emoji: "🎵", color: "#1e3264", q: "pop hits 2024" },
                  { name: "R&B", emoji: "💜", color: "#503750", q: "rnb soul hits" },
                  { name: "Rock", emoji: "🎸", color: "#ba5d07", q: "rock hits" },
                  { name: "K-Pop", emoji: "🇰🇷", color: "#148a08", q: "kpop hits 2024" },
                  { name: "Myanmar", emoji: "🇲🇲", color: "#c6951a", q: "myanmar music 2024" },
                  { name: "Christian", emoji: "✝️", color: "#0d3564", q: "christian music 2024" },
                  { name: "Jazz", emoji: "🎷", color: "#0d3564", q: "jazz hits" },
                  { name: "Classical", emoji: "🎻", color: "#006450", q: "classical music" },
                  { name: "Electronic", emoji: "🎛️", color: "#1a1a2e", q: "electronic music 2024" },
                  { name: "Latin", emoji: "💃", color: "#7b2d00", q: "latin hits 2024" },
                ].map(cat => (
                  <div key={cat.name} onClick={() => { setQuery(cat.q); searchSongs(cat.q); }}
                    style={{ background: cat.color, borderRadius: 10, padding: "16px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 64, position: "relative", overflow: "hidden" }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{cat.name}</span>
                    <span style={{ fontSize: 28, transform: "rotate(15deg)" }}>{cat.emoji}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── FEED PAGE ── */}
     {page === "feed" && <MusicFeed user={user} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} onViewProfile={(artistId) => { setViewingProfileId(artistId); setPage("public-profile"); }} highlightedSongId={highlightedSongId} onClearHighlight={() => setHighlightedSongId(null)} />}

      {/* ── CHARTS PAGE ── */}
      {page === "charts" && <Charts onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} />}

      {/* ── CREATE PAGE ── */}
      {page === "create" && <CreateTab user={user} onBack={() => setPage("home")} />}

      {/* ── LIBRARY PAGE ── */}
      {page === "library" && (
        <div style={{ padding: 16 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{t?.library || "Library"}</div>
          <div style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>{playlists.length} playlists</div>
          <div onClick={() => setPage("liked")} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#111", borderRadius: 12, cursor: "pointer", marginBottom: 10, border: "1px solid #1a1a1a" }}>
            <div style={{ width: 52, height: 52, borderRadius: 10, background: "linear-gradient(135deg, #450af5, #c4efd9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>♥</div>
            <div><div style={{ fontWeight: 600, fontSize: 14 }}>Liked Songs</div><div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{likedSongs.length} songs</div></div>
          </div>
          {playlists.map(pl => (
            <div key={pl.id} onClick={() => { setSelectedPlaylist(pl); setPage("playlist"); setBackPage("library"); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#111", borderRadius: 12, cursor: "pointer", marginBottom: 10, border: "1px solid #1a1a1a" }}>
              <div style={{ width: 52, height: 52, borderRadius: 10, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🎵</div>
              <div><div style={{ fontWeight: 600, fontSize: 14 }}>{pl.name}</div><div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{pl.songs.length} songs</div></div>
            </div>
          ))}
          <div onClick={() => setShowPlaylistModal(true)} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#0a0a0a", borderRadius: 12, cursor: "pointer", border: "1px dashed #2a2a2a" }}>
            <div style={{ width: 52, height: 52, borderRadius: 10, background: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>➕</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#1db954" }}>Create Playlist</div>
          </div>
        </div>
      )}

      {/* ── LIKED SONGS ── */}
      {page === "liked" && (
        <div>
          <div style={{ background: "linear-gradient(135deg, #450af5, #c4efd9)", padding: "24px 16px", marginBottom: 16 }}>
            <button style={S.backBtn} onClick={() => setPage("library")} ><svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg></button>
            <div style={{ fontSize: 40, margin: "16px 0 8px" }}>♥</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800 }}>Liked Songs</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{likedSongs.length} songs</div>
          </div>
          {likedSongs.length === 0 ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#555" }}>Songs you like will appear here</div> :
            likedSongs.map((s, i) => <SongRow key={s.trackId} song={s} index={i} queue={likedSongs} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />)}
        </div>
      )}

      {/* ── PLAYLIST PAGE ── */}
      {page === "playlist" && selectedPlaylist && (
        <div>
          <div style={{ background: "#111", padding: "24px 16px", marginBottom: 16 }}>
            <button style={S.backBtn} onClick={() => setPage("library")}><svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg></button>
            <div style={{ fontSize: 40, margin: "16px 0 8px" }}>🎵</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800 }}>{selectedPlaylist.name}</div>
            <div style={{ fontSize: 13, color: "#888" }}>{selectedPlaylist.songs.length} songs</div>
            <button onClick={() => deletePlaylist(selectedPlaylist.id)} style={{ marginTop: 12, background: "none", border: "1px solid #444", color: "#888", padding: "6px 14px", borderRadius: 50, fontSize: 12, cursor: "pointer" }}>Delete playlist</button>
          </div>
          {selectedPlaylist.songs.length === 0 ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#555" }}>Add songs using the ⋮ menu</div> :
            selectedPlaylist.songs.map((s, i) => <SongRow key={s.trackId} song={s} index={i} queue={selectedPlaylist.songs}onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />)}
        </div>
      )}

      {/* ── ARTIST PAGE ── */}
      {page === "artist" && artist && (
        <div>
          <div style={{ background: "linear-gradient(180deg, #333 0%, #000 100%)", padding: "24px 16px 20px" }}>
            <button style={S.backBtn} onClick={() => setPage(backPage)}><svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg></button>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16 }}>
              <img src={artist.artworkUrl100} alt="" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover" }} />
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800 }}>{artist.artistName}</div>
                <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{artistSongs.length} songs</div>
              </div>
            </div>
          </div>
          {loading ? <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /></div> :
            artistSongs.map((s, i) => <SongRow key={s.trackId} song={s} index={i} queue={artistSongs} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />)}
        </div>
      )}

      {/* ── ALBUM PAGE ── */}
      {page === "album" && album && (
        <div>
          <div style={{ background: "linear-gradient(180deg, #333 0%, #000 100%)", padding: "24px 16px 20px" }}>
            <button style={S.backBtn} onClick={() => setPage(backPage)}><svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg></button>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16 }}>
              <img src={album.artworkUrl100?.replace("100x100","300x300")} alt="" style={{ width: 80, height: 80, borderRadius: 10, objectFit: "cover" }} />
              <div>
                <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em" }}>Album</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginTop: 4 }}>{album.collectionName}</div>
                <div onClick={() => openArtist(album)} style={{ fontSize: 13, color: "#888", marginTop: 4, cursor: "pointer" }}>{album.artistName}</div>
              </div>
            </div>
          </div>
          {loading ? <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /></div> :
            albumSongs.map((s, i) => <SongRow key={s.trackId} song={s} index={i} queue={albumSongs} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} likedSongs={likedSongs} onToggleLike={toggleLike} onOpenArtist={openArtist} onOpenAlbum={openAlbum} uid={uid} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} playlists={playlists} onAddToPlaylist={(song) => { setAddToPlaylistSong(song); setShowPlaylistModal(true); }} />)}
        </div>
      )}

     {/* ── PUBLIC PROFILE PAGE ── */}
      {page === "public-profile" && viewingProfileId && (
<PublicProfile artistId={viewingProfileId} onBack={() => setPage("feed")} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} db={db} currentUserId={uid} />
      )}
     {/* ── PROFILE PAGE ── */}
      {page === "profile" && (
        <div style={{ paddingBottom: 100, overflow: "visible" }}>
          {/* Cover Banner */}
         <div style={{ height: 130, background: userFirebaseProfile?.coverPhoto ? `url(${userFirebaseProfile.coverPhoto}) center/cover no-repeat` : "linear-gradient(135deg, #0d2818, #1a4a2a)", position: "relative" }}>
<label style={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }}>
  <div style={{ background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", padding: "5px 10px", borderRadius: 16, fontSize: 11, cursor: "pointer" }}>Edit Cover</div>
  <input type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "mymusic_unsigned");
    formData.append("cloud_name", "dsczkplah");
    try {
      const r = await fetch("https://api.cloudinary.com/v1_1/dsczkplah/image/upload", { method: "POST", body: formData });
      const d = await r.json();
      await setDoc(doc(db, "users", user.uid), { coverPhoto: d.secure_url }, { merge: true });
      const updatedDoc = await getDoc(doc(db, "users", user.uid));
      if (updatedDoc.exists()) setUserFirebaseProfile(updatedDoc.data());
      setEditToast(true); setTimeout(() => setEditToast(false), 3000);
    } catch { alert("Failed to upload cover photo."); }
  }} />
</label>
          </div>

          {/* Profile Info — centered */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 16px 16px", marginTop: -20, overflow: "visible" }}>
            {/* Avatar */}
           <div style={{ width: 90, height: 90, borderRadius: "50%", border: "3px solid #000", background: "#111", overflow: "hidden", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 44, height: 44, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
            </div>

            {/* Name */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800 }}>{user.displayName || user.email?.split("@")[0]}</div>
              {userFirebaseProfile?.verified && (
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#1d9bf0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 24 24" style={{ width: 10, height: 10, fill: "none", stroke: "#fff", strokeWidth: 3 }}><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              )}
            </div>

            {/* Username */}
            <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
  {userFirebaseProfile?.umsId ? `@${userFirebaseProfile.umsId}` : `@${(user.displayName || user.email?.split("@")[0] || "user").toLowerCase().replace(/\s+/g, "")}`}
</div>

           {/* Premium badge */}
            {userFirebaseProfile?.premium && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg, #1db954, #0a8f3c)", borderRadius: 50, padding: "3px 10px", marginBottom: 8 }}>
                <span style={{ fontSize: 11 }}>⭐</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>MyMusic Premium</span>
              </div>
            )}
            {/* Founding Member badge */}
            {userFirebaseProfile?.foundingMember && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg, #ffd700, #f59e0b)", borderRadius: 50, padding: "3px 10px", marginBottom: 8 }}>
                <span style={{ fontSize: 11 }}>⭐</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#000" }}>Founding Member</span>
              </div>
            )}

            {/* Bio */}
            {userFirebaseProfile?.bio && (
              <div style={{ fontSize: 13, color: "#aaa", textAlign: "center", marginBottom: 10, lineHeight: 1.5 }}>{userFirebaseProfile.bio}</div>
            )}

            {/* Followers · Following */}
            <div style={{ fontSize: 13, color: "#aaa", marginBottom: 16 }}>
              <span style={{ color: "#fff", fontWeight: 700 }}>{userFirebaseProfile?.followers || 0}</span> Followers &nbsp;·&nbsp; <span style={{ color: "#fff", fontWeight: 700 }}>{userFirebaseProfile?.following || 0}</span> Following
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <button onClick={() => { const url = `https://mymusicstream.online/@${(userFirebaseProfile?.username || user.displayName || "user").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "")}`; if (navigator.share) navigator.share({ title: `${user.displayName} on MyMusic`, url }); else { navigator.clipboard.writeText(url); alert("Profile link copied!"); } }}
                style={{ flex: 1, padding: "10px", borderRadius: 50, background: "#1db954", border: "none", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Share Profile
              </button>
              <button onClick={() => setShowEditProfile(true)}
                style={{ flex: 1, padding: "10px", borderRadius: 50, background: "transparent", border: "1px solid #333", color: "#fff", fontSize: 13, cursor: "pointer" }}>
                Edit Profile
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderTop: "1px solid #111", borderBottom: "1px solid #111", marginBottom: 16 }}>
            {["songs", "liked", "about"].map(t => (
              <div key={t} onClick={() => setProfileTab(t)} style={{ flex: 1, textAlign: "center", padding: "12px", fontSize: 13, fontWeight: profileTab === t ? 700 : 400, color: profileTab === t ? "#1db954" : "#555", borderBottom: profileTab === t ? "2px solid #1db954" : "2px solid transparent", cursor: "pointer", textTransform: "capitalize" }}>{t}</div>
            ))}
          </div>

          <div style={{ padding: "0 16px" }}>
</div>
{(() => {
  const searches = JSON.parse(localStorage.getItem(`searches_${uid}`) || "[]");
  const allText = [...searches.map(s => s.query), ...recentlyPlayed.map(s => s.trackName + " " + s.artistName), ...likedSongs.map(s => s.trackName + " " + s.artistName)].join(" ").toLowerCase();
  const genres = [
    { name: "K-Pop Stan", keywords: ["kpop", "bts", "blackpink", "twice", "exo"], emoji: "🇰🇷", color: "#8b5cf6" },
    { name: "Myanmar Music Lover", keywords: ["myanmar", "htoi", "lay phyu", "chit thu"], emoji: "🇲🇲", color: "#f59e0b" },
    { name: "R&B Soul", keywords: ["rnb", "r&b", "soul", "beyonce", "frank ocean", "sza"], emoji: "💜", color: "#ec4899" },
    { name: "Hip Hop Head", keywords: ["hip hop", "rap", "drake", "kendrick", "travis"], emoji: "🎤", color: "#6d28d9" },
    { name: "Pop Lover", keywords: ["pop", "taylor", "ariana", "billie", "dua lipa"], emoji: "✨", color: "#1d9bf0" },
    { name: "Rock Legend", keywords: ["rock", "metal", "linkin park", "coldplay", "nirvana"], emoji: "🎸", color: "#e91429" },
    { name: "Christian Worshipper", keywords: ["christian", "worship", "gospel", "hillsong"], emoji: "✝️", color: "#3b82f6" },
    { name: "Global Explorer", keywords: ["world", "latin", "jazz", "classical", "indie"], emoji: "🌍", color: "#1db954" },
  ];
  let topGenre = genres[genres.length - 1];
  let topScore = 0;
  genres.forEach(g => { const score = g.keywords.filter(k => allText.includes(k)).length; if (score > topScore) { topScore = score; topGenre = g; } });
 const pct = Math.min(95, Math.max(60, topScore * 15 + 60));
  
  // Only show after at least 1 week of listening history
  const oldestPlay = recentlyPlayed.length > 0 
    ? Math.min(...recentlyPlayed.map(s => s.playedAt?.seconds ? s.playedAt.seconds * 1000 : Date.now()))
    : Date.now();
  const daysSinceFirstPlay = (Date.now() - oldestPlay) / (1000 * 60 * 60 * 24);
  const hasEnoughHistory = recentlyPlayed.length >= 5 && daysSinceFirstPlay >= 7;
  
  if (!hasEnoughHistory) return (
    <div style={{ marginBottom: 20, borderRadius: 16, padding: 20, border: "1px solid #1a1a1a", background: "#0a0a0a" }}>
      {/* Tab Content */}
{profileTab === "songs" && (
  <div style={{ padding: "0 16px", marginBottom: 16 }}>
    <ProfileSongs uid={uid} db={db} currentSong={currentSong} onPlay={(s, queue) => playSong(s, queue)} />
  </div>
)}

{profileTab === "liked" && (
  <div style={{ padding: "0 16px", marginBottom: 16 }}>
    {likedSongs.length === 0 ? (
      <div style={{ textAlign: "center", padding: "40px 20px", color: "#555" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>❤️</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>No liked songs yet</div>
        <div style={{ fontSize: 13 }}>Songs you like will appear here</div>
      </div>
    ) : likedSongs.slice(0, 20).map((s, i) => (
      <div key={s.trackId} onClick={() => playSong(s, likedSongs)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
        <div style={{ fontSize: 12, color: "#555", width: 20, textAlign: "center" }}>{i + 1}</div>
        <img src={s.artworkUrl100} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: currentSong?.trackId === s.trackId ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.trackName}</div>
          <div style={{ fontSize: 12, color: "#555" }}>{s.artistName}</div>
        </div>
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#e91429", stroke: "#e91429", strokeWidth: 1.5, flexShrink: 0 }}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
      </div>
    ))}
  </div>
)}

{profileTab === "about" && (
  <div style={{ padding: "0 16px", marginBottom: 16 }}>
    <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>Bio</div>
      <div style={{ fontSize: 14, color: userFirebaseProfile?.bio ? "#ccc" : "#555", lineHeight: 1.6 }}>{userFirebaseProfile?.bio || "No bio yet. Tap Edit Profile to add one."}</div>
    </div>
    <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a" }}>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>Stats</div>
      {[
        { label: "Songs played", value: recentlyPlayed.length },
        { label: "Songs liked", value: likedSongs.length },
        { label: "Playlists", value: playlists.length },
        { label: "Member since", value: userFirebaseProfile?.createdAt ? new Date(userFirebaseProfile.createdAt).toLocaleDateString() : "—" },
      ].map(({ label, value }) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #0a0a0a" }}>
          <span style={{ fontSize: 13, color: "#555" }}>{label}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{value}</span>
        </div>
      ))}
    </div>
  </div>
)}
      <div style={{ fontSize: 10, color: "#444", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>🎵 YOUR MUSIC PERSONALITY</div>
      <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
        Keep listening! Your Music Personality unlocks after <span style={{ color: "#fff" }}>7 days</span> of listening history.
      </div>
      <div style={{ marginTop: 12, background: "#111", borderRadius: 50, height: 4 }}>
        <div style={{ background: "#1db954", height: "100%", borderRadius: 50, width: `${Math.min(100, (recentlyPlayed.length / 5) * 100)}%`, transition: "width 1s ease" }} />
      </div>
      <div style={{ fontSize: 11, color: "#444", marginTop: 6 }}>{recentlyPlayed.length}/5 songs played minimum</div>
    </div>
  );

  return (
    <div style={{ marginBottom: 20, borderRadius: 16, padding: 20, border: `1px solid ${topGenre.color}55`, background: "#0a0a0a", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -30, right: -30, fontSize: 120, opacity: 0.06, lineHeight: 1 }}>{topGenre.emoji}</div>
      <div style={{ fontSize: 10, color: topGenre.color, fontWeight: 700, letterSpacing: "0.15em", marginBottom: 12 }}>🎵 YOUR MUSIC PERSONALITY</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 13, color: "#666", marginBottom: 4 }}>You are a</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
        {topGenre.emoji} {topGenre.name}
      </div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 16 }}>{pct}% match based on your listening</div>
      <div style={{ background: "#1a1a1a", borderRadius: 50, height: 6, marginBottom: 16, overflow: "hidden" }}>
        <div style={{ background: `linear-gradient(90deg, ${topGenre.color}, ${topGenre.color}88)`, height: "100%", borderRadius: 50, width: `${pct}%` }} />
      </div>
      <button onClick={() => {
        const text = `🎵 My Music Personality on MyMusic:\n${topGenre.emoji} ${topGenre.name} (${pct}% match)\n\nDiscover yours → mymusicstream.online`;
        if (navigator.share) navigator.share({ title: "My Music Personality", text });
        else { navigator.clipboard.writeText(text); alert("Copied! Share with friends 🎵"); }
      }} style={{ padding: "8px 20px", borderRadius: 50, border: "none", background: topGenre.color, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
        Share My Personality 📱
      </button>
    </div>
  );
})()}
         {/* Monthly Wrapped */}
{(() => {
  const now = new Date();
  const monthName = now.toLocaleString("default", { month: "long" });
  const year = now.getFullYear();
  const topSong = recentlyPlayed[0];
  const topArtist = recentlyPlayed.length > 0
    ? Object.entries(recentlyPlayed.reduce((acc, s) => { acc[s.artistName] = (acc[s.artistName] || 0) + 1; return acc; }, {})).sort((a, b) => b[1] - a[1])[0]?.[0]
    : null;
  const totalPlays = recentlyPlayed.length;
  if (totalPlays === 0) return null;
  return (
    <div style={{ marginBottom: 20, borderRadius: 16, overflow: "hidden", border: "1px solid #1a1a1a" }}>
      {/* Header */}
      <div style={{ background: "#111", padding: "16px 20px 12px", borderBottom: "1px solid #1a1a1a" }}>
        <div style={{ fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 4 }}>YOUR MONTHLY RECAP</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>{monthName} {year}</div>
      </div>

      {/* Stats */}
      <div style={{ background: "#0a0a0a", padding: "16px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "#555" }}>Songs played</span>
            <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, color: "#fff" }}>{totalPlays}</span>
          </div>
          {topSong && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#555", flexShrink: 0 }}>Top song</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <img src={topSong.artworkUrl100} alt="" style={{ width: 32, height: 32, borderRadius: 6, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{topSong.trackName}</span>
              </div>
            </div>
          )}
          {topArtist && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, color: "#555" }}>Top artist</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{topArtist}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "#555" }}>Liked songs</span>
            <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, color: "#e91429" }}>{likedSongs.length}</span>
          </div>
        </div>
      </div>

      {/* Share button */}
      <div style={{ background: "#111", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: "#555" }}>Share your {monthName} recap</span>
        <button onClick={() => {
          const text = `🎵 My ${monthName} ${year} on MyMusic:\n\n🎧 ${totalPlays} songs played\n${topSong ? `🎵 Top song: ${topSong.trackName}\n` : ""}${topArtist ? `🎤 Top artist: ${topArtist}\n` : ""}❤️ ${likedSongs.length} songs liked\n\nmymusicstream.online`;
          if (navigator.share) navigator.share({ title: `My ${monthName} Wrapped`, text });
          else { navigator.clipboard.writeText(text); alert("Copied! Share it 🎵"); }
        }} style={{ padding: "8px 16px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Share Recap
        </button>
      </div>
    </div>
  );
})()}
          {/* UMS Banner */}
          <div onClick={() => setShowUMS(true)} style={{ marginBottom: 20, background: "linear-gradient(135deg, #0d2818, #111)", borderRadius: 16, padding: 20, border: "1px solid #1db954", cursor: "pointer" }}>
            <div style={{ fontSize: 10, color: "#1db954", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>🌍 UNIVERSAL MUSIC SOCIETY</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Get Your Music ID</div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>Join the world's first global music community. Apply for your official Music ID.</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#1db954", borderRadius: 50, padding: "8px 16px" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>Apply Now →</span>
            </div>
          </div>
          
           {/* Referral Card */}
<div style={{ marginBottom: 20, background: "#0a0a0a", borderRadius: 16, padding: 20, border: "1px solid #1a1a1a" }}>
  <div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>⭐ FOUNDING MEMBER PROGRAM</div>
  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Invite friends, earn your legacy</div>
  <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7, marginBottom: 14 }}>Share your link. When friends join, you both get the exclusive Founding Member badge — limited to first 500 members only.</div>
  <div style={{ background: "#111", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, border: "1px solid #1a1a1a" }}>
    <span style={{ fontSize: 12, color: "#888", fontFamily: "monospace" }}>mymusicstream.online/?ref={uid.slice(0, 8)}</span>
    <button onClick={() => { navigator.clipboard.writeText(`https://mymusicstream.online/?ref=${uid}`); alert("Referral link copied! Share it with friends 🎵"); }}
      style={{ background: "none", border: "none", color: "#1db954", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
      Copy
    </button>
  </div>
  <button onClick={() => {
    const text = `🎵 Join me on MyMusic — the first music app with a Universal Music Society ID!\n\nSign up here and we both get the exclusive Founding Member badge:\nhttps://mymusicstream.online/?ref=${uid}\n\nLimited to first 500 members only! 🏆`;
    if (navigator.share) navigator.share({ title: "Join MyMusic", text });
    else { navigator.clipboard.writeText(text); alert("Copied! Share it with friends 🎵"); }
  }} style={{ width: "100%", padding: "12px", borderRadius: 50, border: "none", background: "linear-gradient(135deg, #ffd700, #f59e0b)", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
    ⭐ Share My Referral Link
  </button>
</div>
 
         {user.email === "lawhaling91069@gmail.com" && (
  <div onClick={() => setShowAdminDashboard(true)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer", marginBottom: 4 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #f59e0b, #e13300)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#fff", strokeWidth: 2 }}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
      </div>
      <span style={{ fontSize: 14, fontWeight: 600, color: "#f59e0b" }}>Admin Dashboard</span>
    </div>
    <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#555", strokeWidth: 2 }}><polyline points="9 18 15 12 9 6"/></svg>
  </div>
)}

{[
         { label: "🎤 Artist Profile", action: () => setPage("artist-profile") },
            { label: "🌍 Music ID (UMS)", action: () => setShowUMS(true) },
            { label: "⭐ Premium", action: () => setShowPremium(true) },
            { label: "⚙️ Settings", action: () => setShowSettings(true) },
            { label: "📜 Terms of Service", action: () => setPage("terms") },
            { label: "🔒 Privacy Policy", action: () => setPage("privacy") },
            { label: "ℹ️ About MyMusic", action: () => setPage("about") },
          ].map(item => (
            <div key={item.label} onClick={item.action} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
              <span style={{ fontSize: 14 }}>{item.label}</span>
              <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#555", strokeWidth: 2 }}><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          ))}

          <button onClick={() => signOut(auth)} style={{ width: "100%", marginTop: 24, padding: 14, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#e91429", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Sign Out
          </button>
        </div>
      )}

      {/* ── ARTIST PROFILE PAGE ── */}
      {page === "artist-profile" && <ArtistProfile user={user} onBack={() => setPage("profile")} onPlaySong={(song, queue) => { playSong(song, queue); setShowFullPlayer(true); }} currentSong={currentSong} />}

      {/* ── TERMS / PRIVACY / ABOUT ── */}
      {page === "terms" && (
        <div style={{ padding: "20px 16px 40px" }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, letterSpacing: "-0.5px", marginBottom: 4 }}>Terms of Service</div>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 32 }}>Last updated: June 2026 · mymusicstream.online</div>
          {[
            { title: "1. About MyMusic", content: "MyMusic is an independent music streaming and social platform available at mymusicstream.online. We are not a registered company — MyMusic is an independent online service. By using MyMusic, you accept these terms." },
            { title: "2. Who Can Use MyMusic", content: "You must be at least 13 years old to use MyMusic. By creating an account, you confirm that you meet this requirement." },
            { title: "3. Uploading Music", content: "Artists may upload original music they own or have full rights to. By uploading, you confirm that you own or have the legal rights to the music and it does not infringe on any copyright." },
            { title: "4. Prohibited Content", content: "Strictly prohibited: uploading music you do not own, sharing or promoting illegal activities, hate speech or harassment, sexual content involving minors, spam or fake accounts. Violations result in immediate account suspension." },
            { title: "5. Community Standards", content: "MyMusic is a music community. We expect all members to treat each other with respect. Harassment, bullying, threats or abusive behaviour toward other users will not be tolerated." },
            { title: "6. Contact", content: "For any questions about these Terms, contact us at support@mymusicstream.online." },
          ].map(item => (
            <div key={item.title} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid #111" }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: "#fff" }}>{item.title}</div>
              <div style={{ fontSize: 13, color: "#888", lineHeight: 1.85 }}>{item.content}</div>
            </div>
          ))}
        </div>
      )}

      {page === "privacy" && (
        <div style={{ padding: "20px 16px 40px" }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, letterSpacing: "-0.5px", marginBottom: 4 }}>Privacy Policy</div>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 32 }}>Last updated: June 2026</div>
          {[
            { title: "1. What We Collect", content: "When you create an account, we collect your email address, display name, and profile photo if you choose to upload one." },
            { title: "2. How We Use It", content: "We use your information to create and manage your account, send account-related emails, improve the app experience, and prevent fraud." },
            { title: "3. Data Sharing", content: "We do not sell, rent or share your personal data with advertisers or third parties for marketing purposes." },
            { title: "4. Your Rights", content: "You have the right to access, correct, or delete your personal data at any time. To delete your account go to Settings." },
            { title: "5. Contact", content: "For privacy questions, contact us at support@mymusicstream.online." },
          ].map(item => (
            <div key={item.title} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid #111" }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: "#fff" }}>{item.title}</div>
              <div style={{ fontSize: 13, color: "#888", lineHeight: 1.85 }}>{item.content}</div>
            </div>
          ))}
        </div>
      )}

      {page === "about" && (
        <div style={{ paddingBottom: 40 }}>
          <div style={{ background: "#000", padding: "48px 24px 40px", textAlign: "center" }}>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, color: "#fff", letterSpacing: "-0.5px", marginBottom: 12 }}>MyMusic</div>
            <div style={{ fontSize: 13, color: "#666", lineHeight: 1.8, maxWidth: 320, margin: "0 auto" }}>The first music platform built around identity, community and discovery.</div>
          </div>
          <div style={{ padding: "40px 24px 0" }}>
            <p style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 22, lineHeight: 1.5, color: "#fff", marginBottom: 20 }}>"Music was never meant to be experienced alone."</p>
            <p style={{ fontSize: 14, color: "#666", lineHeight: 1.85, marginBottom: 32 }}>MyMusic was built because music deserved something better than a passive listening app. The Universal Music Society is the world's first global music membership.</p>
            <div style={{ background: "#111", borderRadius: 12, padding: 24, marginBottom: 24, border: "1px solid #1a1a1a" }}>
              <p style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 18, color: "#fff", lineHeight: 1.6 }}>To connect every music lover and artist on earth through a platform that treats music as identity, community and culture.</p>
            </div>
            <div onClick={() => window.open("mailto:support@mymusicstream.online")} style={{ display: "flex", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid #111", cursor: "pointer" }}>
              <span style={{ fontSize: 13, color: "#999" }}>Email</span>
              <span style={{ fontSize: 13, color: "#fff" }}>support@mymusicstream.online</span>
            </div>
            <div style={{ textAlign: "center", padding: "32px 0 8px" }}>
              <p style={{ color: "#333", fontSize: 12 }}>© 2026 MyMusic · mymusicstream.online</p>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD TO PLAYLIST MODAL ── */}
      {showAddToPlaylist && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#1a1a1a", borderRadius: 16, padding: 24, width: "100%", maxWidth: 340, border: "1px solid #2a2a2a" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Add to Playlist</div>
            {playlists.length === 0 ? <p style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>No playlists yet!</p> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {playlists.map(p => (
                  <div key={p.id} onClick={() => addToPlaylist(p.id, showAddToPlaylist)} style={{ padding: "12px 16px", background: "#111", borderRadius: 10, cursor: "pointer", fontSize: 14 }}>🎵 {p.name} ({p.songs.length} songs)</div>
                ))}
              </div>
            )}
            <button onClick={() => setShowAddToPlaylist(null)} style={{ width: "100%", padding: 12, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── CREATE/ADD PLAYLIST MODAL ── */}
      {showPlaylistModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => { setShowPlaylistModal(false); setAddToPlaylistSong(null); }}>
          <div style={{ background: "#1a1a1a", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", border: "1px solid #2a2a2a", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            
            {addToPlaylistSong ? (
              <>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Add to Playlist</div>
                <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>"{addToPlaylistSong.trackName}"</div>
                {playlists.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "#555", fontSize: 13 }}>No playlists yet. Create one first!</div>
                ) : playlists.map(pl => (
                  <div key={pl.id} onClick={() => {
                    const updated = playlists.map(p => p.id === pl.id ? { ...p, songs: [...p.songs.filter(s => s.trackId !== addToPlaylistSong.trackId), addToPlaylistSong] } : p);
                    setPlaylists(updated);
                    localStorage.setItem(`playlists_${uid}`, JSON.stringify(updated));
                    setShowPlaylistModal(false);
                    setAddToPlaylistSong(null);
                    alert(`Added to "${pl.name}"!`);
                  }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #111", cursor: "pointer" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎵</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{pl.name}</div>
                      <div style={{ fontSize: 12, color: "#555" }}>{pl.songs.length} songs</div>
                    </div>
                  </div>
                ))}
                <button onClick={() => { setAddToPlaylistSong(null); }} style={{ width: "100%", marginTop: 14, padding: 12, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>Cancel</button>
              </>
            ) : (
              <>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Create Playlist</div>
                <input value={newPlaylistName} onChange={e => setNewPlaylistName(e.target.value)} placeholder="Playlist name..." onKeyDown={e => e.key === "Enter" && createPlaylist()}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", marginBottom: 16 }} />
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setShowPlaylistModal(false)} style={{ flex: 1, padding: 12, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  <button onClick={createPlaylist} style={{ flex: 1, padding: 12, borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Create</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── FULL SCREEN PLAYER ── */}
      {showFullPlayer && currentSong && (
<div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 200, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 16px", flexShrink: 0 }}>
            <button onClick={() => setShowFullPlayer(false)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
              <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div style={{ fontSize: 12, color: "#888", fontWeight: 600, letterSpacing: "0.1em" }}>NOW PLAYING</div>
            <button onClick={() => setShowKaraoke(true)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 20 }}>🎤</button>
            <button onClick={() => setShowMusicAI(true)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 6 }}>
  <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: "#888", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><circle cx="9" cy="10" r="1" fill="#888"/><circle cx="12" cy="10" r="1" fill="#888"/><circle cx="15" cy="10" r="1" fill="#888"/></svg>
</button>
          </div>

          <div style={{ padding: "0 32px 24px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <img src={currentSong.artworkUrl100?.replace("100x100","600x600")} alt="" style={{ width: "100%", maxWidth: 280, borderRadius: 16, boxShadow: "0 32px 64px rgba(0,0,0,0.8)" }} onError={e => e.target.src = currentSong.artworkUrl100} />
          </div>

          <div style={{ padding: "0 24px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentSong.trackName}</div>
              <div style={{ fontSize: 14, color: "#888", marginTop: 4 }}>{currentSong.artistName}</div>
            </div>
            <button onClick={() => toggleLike(currentSong)} style={{ background: "none", border: "none", fontSize: 26, cursor: "pointer", color: likedSongs.some(s => s.trackId === currentSong.trackId) ? "#e91429" : "#555", flexShrink: 0, marginLeft: 16 }}>
              {likedSongs.some(s => s.trackId === currentSong.trackId) ? "♥" : "♡"}
            </button>
          </div>

          <div style={{ padding: "0 24px 20px", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#555", minWidth: 35 }}>{fmt(currentTime)}</span>
             <input
                type="range"
                min="0"
                max={duration || 0}
                step="0.1"
                value={currentTime}
                onChange={e => {
                  const newTime = parseFloat(e.target.value);
                  setCurrentTime(newTime);
                }}
                onMouseUp={e => { playerRef.current?.seekTo(parseFloat(e.target.value), true); }}
                onTouchEnd={e => { playerRef.current?.seekTo(parseFloat(e.target.value), true); }}
                style={{
                  flex: 1, height: 4, borderRadius: 2, appearance: "none", WebkitAppearance: "none",
                  background: `linear-gradient(to right, #fff 0%, #fff ${duration ? (currentTime / duration) * 100 : 0}%, #2a2a2a ${duration ? (currentTime / duration) * 100 : 0}%, #2a2a2a 100%)`,
                  cursor: "pointer", outline: "none"
                }}
              />
              <span style={{ fontSize: 11, color: "#555", minWidth: 35, textAlign: "right" }}>{fmt(duration)}</span>
            </div>
          </div>

          <div style={{ padding: "0 24px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
<button onClick={() => setShuffle(!shuffle)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
  <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: shuffle ? "#1db954" : "#555", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
</button>
<button onClick={playPrev} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
  <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, fill: "#fff" }}><polygon points="19 20 9 12 19 4 19 20"/><line x1="5" y1="19" x2="5" y2="5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
</button>
<button onClick={togglePlay} style={{ width: 64, height: 64, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(255,255,255,0.2)" }}>
  {isPlaying ? (
    <svg viewBox="0 0 24 24" style={{ width: 26, height: 26, fill: "#000" }}><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
  ) : (
    <svg viewBox="0 0 24 24" style={{ width: 26, height: 26, fill: "#000", marginLeft: 3 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
  )}
</button>
<button onClick={playNext} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
  <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, fill: "#fff" }}><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
</button>
<button onClick={() => setRepeat(!repeat)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
  <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: repeat ? "#1db954" : "#555", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
</button>
          </div>

          <div style={{ padding: "0 20px 16px", flexShrink: 0 }}>
            <div style={{ display: "flex", background: "#111", borderRadius: 50, padding: 4, gap: 2 }}>
              {["related", "lyrics", "key", "queue"].map(tab => (
                <button key={tab} onClick={() => setShowTab(tab)} style={{ flex: 1, padding: "8px 4px", borderRadius: 50, border: "none", background: showTab === tab ? "#fff" : "transparent", color: showTab === tab ? "#000" : "#666", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  {tab === "key" ? "🎵 Key" : tab === "queue" ? `Queue (${currentQueue.length})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: "0 20px 120px", flexShrink: 0 }}>
            {showTab === "related" && (
              <>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Related Songs</div>
                {relatedSongs.map(s => (
                  <div key={s.trackId} onClick={() => playSong(s, relatedSongs)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", cursor: "pointer", borderBottom: "1px solid #0a0a0a" }}>
                    <img src={s.artworkUrl100} alt="" style={{ width: 46, height: 46, borderRadius: 6, objectFit: "cover" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: currentSong?.trackId === s.trackId ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.trackName}</div>
                      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{s.artistName}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
            {showTab === "lyrics" && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800 }}>Lyrics</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#555" }}>
                    Delay:
                    <input type="range" min="-5" max="5" step="0.1" value={lyricsOffset} onChange={e => setLyricsOffset(Number(e.target.value))} style={{ width: 80 }} />
                    {lyricsOffset > 0 ? "+" : ""}{lyricsOffset}s
                  </div>
                </div>
                {lyricsLoading ? <p style={{ color: "#555", fontSize: 14 }}>Loading lyrics...</p> : <p style={{ color: "#ccc", fontSize: 15, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{lyrics}</p>}
              </div>
            )}
            {showTab === "key" && (
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 4 }}>Song Key & Chords</div>
                <p style={{ fontSize: 12, color: "#555", marginBottom: 16 }}>Play along with {currentSong?.trackName}</p>
               {songKeyLoading ? (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0", gap: 12 }}>
    <div style={{ width: 24, height: 24, border: "2px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <span style={{ fontSize: 13, color: "#555" }}>Generating chord sheet...</span>
  </div>
) : !songKey ? (
  <div style={{ textAlign: "center", padding: "32px 0" }}>
    <button onClick={() => fetchSongKey(currentSong)} style={{ padding: "12px 24px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
      Generate Chord Sheet
    </button>
  </div>
) : (
  <div>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1db954" }} />
        <span style={{ fontSize: 11, color: "#1db954", fontWeight: 600 }}>AI CHORD SHEET</span>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => fetchSongKey(currentSong)} style={{ padding: "5px 12px", borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#555", fontSize: 11, cursor: "pointer" }}>Refresh</button>
        <button onClick={() => {
          if (navigator.share) navigator.share({ title: `${currentSong.trackName} Chord Sheet`, text: songKey });
          else { navigator.clipboard.writeText(songKey); alert("Chord sheet copied!"); }
        }} style={{ padding: "5px 12px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Share</button>
      </div>
    </div>
    <div style={{ background: "#0a0a0a", borderRadius: 12, padding: 16, border: "1px solid #1a1a1a", overflowX: "auto" }}>
      <pre style={{ color: "#ccc", fontSize: 13, lineHeight: 2, whiteSpace: "pre-wrap", fontFamily: "monospace", margin: 0 }}>{songKey}</pre>
    </div>
  </div>
)}
              </div>
            )}
            {showTab === "queue" && (
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 4 }}>Up Next</div>
                <div style={{ fontSize: 12, color: "#555", marginBottom: 14 }}>{currentQueue.length} songs in queue</div>
                {currentQueue.length === 0 ? <p style={{ color: "#555", fontSize: 13 }}>No songs in queue.</p> :
                  currentQueue.map((s, i) => (
                    <div key={s.trackId} onClick={() => { setCurrentIndex(i); fetchAndPlay(s); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer", background: i === currentIndex ? "rgba(255,255,255,0.04)" : "transparent", borderRadius: 8, paddingLeft: i === currentIndex ? 8 : 0 }}>
                      <span style={{ fontSize: 11, color: i === currentIndex ? "#fff" : "#444", minWidth: 20, textAlign: "center" }}>{i === currentIndex ? "▶" : i + 1}</span>
                      <img src={s.artworkUrl100} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: i === currentIndex ? 700 : 400, color: i === currentIndex ? "#fff" : "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.trackName}</div>
                        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{s.artistName}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); setCurrentQueue(prev => prev.filter((_, idx) => idx !== i)); }} style={{ background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: 16 }}>×</button>
                    </div>
                  ))}
                {currentQueue.length > 0 && (
                  <button onClick={() => setCurrentQueue(prev => prev.filter((_, i) => i <= currentIndex))} style={{ marginTop: 16, width: "100%", padding: 12, borderRadius: 10, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 13, cursor: "pointer" }}>Clear queue</button>
                )}
              </div>
            )}
</div>
        </div>
      
    )}

     {/* ── FLOATING AI BUTTON ── */}
<button onClick={() => setShowMusicAI(true)} style={{ position: "fixed", right: 16, bottom: currentSong ? 130 : 80, width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, #1db954, #0a8f3c)", border: "none", cursor: "pointer", zIndex: 499, boxShadow: "0 4px 20px rgba(29,185,84,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
  <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: "#fff", strokeWidth: 1.8 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
</button>
     {/* ── FLOATING CIRCLE PLAYER ── */}
{currentSong && !showFullPlayer && floatingPlayerVisible && (() => {
  const pos = floatingPosState;
  return (
    <div style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 499, userSelect: "none" }}
      onMouseDown={e => {
        e.stopPropagation();
        const startX = e.clientX - pos.x;
        const startY = e.clientY - pos.y;
        let moved = false;
        const onMove = e => {
          moved = true;
          isDragging.current = true;
          setFloatingPosState({
            x: Math.max(0, Math.min(window.innerWidth - 70, e.clientX - startX)),
            y: Math.max(60, Math.min(window.innerHeight - 120, e.clientY - startY))
          });
        };
        const onUp = () => {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
          if (!moved) isDragging.current = false;
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      }}
onTouchStart={e => {
        e.stopPropagation();
        const t = e.touches[0];
        const startX = t.clientX - pos.x;
        const startY = t.clientY - pos.y;
       const onMove = e => {
          e.preventDefault();
          isDragging.current = true;
          const t = e.touches[0];
          setFloatingPosState({
            x: Math.max(0, Math.min(window.innerWidth - 70, t.clientX - startX)),
            y: Math.max(60, Math.min(window.innerHeight - 120, t.clientY - startY))
          });
        };
        const onUp = () => {
          document.removeEventListener("touchmove", onMove);
          document.removeEventListener("touchend", onUp);
          setTimeout(() => { isDragging.current = false; }, 100);
        };
        document.addEventListener("touchmove", onMove, { passive: false });
        document.addEventListener("touchend", onUp);
      }}>

      {/* Circle */}
      <div
        onClick={() => {
          if (isDragging.current) return;
          if (floatingTapRef.current) {
            clearTimeout(floatingTapRef.current);
            floatingTapRef.current = null;
            setFloatingPlayerExpanded(false);
            setShowFullPlayer(true);
          } else {
            floatingTapRef.current = setTimeout(() => {
              floatingTapRef.current = null;
              setFloatingPlayerExpanded(prev => !prev);
            }, 250);
          }
        }}
        style={{ width: 56, height: 56, borderRadius: "50%", overflow: "hidden", border: "2.5px solid #1db954", boxShadow: "0 4px 20px rgba(29,185,84,0.3)", cursor: "pointer", position: "relative" }}>
        {currentSong.artworkUrl100
          ? <img src={currentSong.artworkUrl100} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
          : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#0d2818,#1a4a2a)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🎵</div>}
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          {isPlaying
            ? <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#fff" }}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            : <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#fff" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>}
        </div>
      </div>

      {/* Controls — show horizontally when expanded */}
      {floatingPlayerExpanded && (
        <div style={{ position: "absolute", ...(pos.x > window.innerWidth / 2 ? { right: 64, left: "auto" } : { left: 64 }), top: 8, background: "rgba(0,0,0,0.92)", borderRadius: 50, padding: "8px 14px", display: "flex", alignItems: "center", gap: 14, border: "0.5px solid #333", whiteSpace: "nowrap" }}>
          <button onClick={playPrev} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#fff" }}><polygon points="19 20 9 12 19 4 19 20"/><rect x="5" y="4" width="2" height="16" fill="#fff"/></svg>
          </button>
          <button onClick={togglePlay} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
            {isPlaying
              ? <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "#1db954" }}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "#1db954" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>}
          </button>
          <button onClick={playNext} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#fff" }}><polygon points="5 4 15 12 5 20 5 4"/><rect x="17" y="4" width="2" height="16" fill="#fff"/></svg>
          </button>
          <button onClick={() => { stopSong(); setFloatingPlayerVisible(false); setFloatingPlayerExpanded(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
            <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#888", strokeWidth: 2 }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}
    </div>
  );
})()}
      {/* ── BOTTOM NAV ── */}
     <BottomNav page={page} setPage={setPage} setProfileTab={setProfileTab} currentSong={currentSong} setShowFullPlayer={setShowFullPlayer} setFloatingPlayerVisible={setFloatingPlayerVisible} setFloatingPlayerExpanded={setFloatingPlayerExpanded} onClearSearch={() => { setQuery(""); setSuggestions([]); setSongs([]); }} />
    </div>
  );
}

// ── BOTTOM NAV ────────────────────────────────────────────────────────────────
function ReportsPanel({ db }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadReports(); }, []);

  async function loadReports() {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "reports"), orderBy("createdAt", "desc"), limit(20)));
      setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
    setLoading(false);
  }

  async function deleteContent(report) {
    try {
      await deleteDoc(doc(db, "songs", report.contentId));
      await updateDoc(doc(db, "reports", report.id), { status: "resolved" });
      setReports(prev => prev.filter(r => r.id !== report.id));
      alert("✅ Content deleted.");
    } catch { alert("Failed to delete."); }
  }

  async function dismissReport(reportId) {
    try {
      await updateDoc(doc(db, "reports", reportId), { status: "dismissed" });
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch {}
  }

  return (
    <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800 }}>🚩 Reports</div>
        <button onClick={loadReports} style={{ background: "none", border: "none", color: "#1db954", fontSize: 12, cursor: "pointer" }}>Refresh</button>
      </div>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <div style={{ width: 20, height: 20, border: "2px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : reports.filter(r => r.status === "pending").length === 0 ? (
        <div style={{ textAlign: "center", padding: "16px 0", fontSize: 13, color: "#555" }}>No pending reports 🎉</div>
      ) : (
        reports.filter(r => r.status === "pending").map(report => (
          <div key={report.id} style={{ padding: "12px 0", borderBottom: "1px solid #0a0a0a" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e9142922", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🚩</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#e91429" }}>{report.reason}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                  {report.contentType} · {new Date(report.createdAt).toLocaleDateString()} · by {report.reporterEmail?.split("@")[0]}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => deleteContent(report)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#e91429", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                🗑️ Delete Content
              </button>
              <button onClick={() => dismissReport(report.id)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #2a2a2a", background: "transparent", color: "#555", fontSize: 12, cursor: "pointer" }}>
                ✓ Dismiss
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
function PageWrapper({ children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div>{children}</div>;
}
function FollowButton({ currentUserId, targetUserId, db }) {
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followerCount, setFollowerCount] = useState(0);

  useEffect(() => {
    async function check() {
      try {
        const followDoc = await getDoc(doc(db, "users", currentUserId, "following", targetUserId));
        setFollowing(followDoc.exists());
        const targetDoc = await getDoc(doc(db, "users", targetUserId));
        setFollowerCount(targetDoc.data()?.followers || 0);
      } catch {}
      setLoading(false);
    }
    check();
  }, [currentUserId, targetUserId]);

  async function toggleFollow() {
    setLoading(true);
    try {
      if (following) {
        await deleteDoc(doc(db, "users", currentUserId, "following", targetUserId));
        await deleteDoc(doc(db, "users", targetUserId, "followers", currentUserId));
        await updateDoc(doc(db, "users", targetUserId), { followers: increment(-1) });
        await updateDoc(doc(db, "users", currentUserId), { following: increment(-1) });
        setFollowerCount(c => Math.max(0, c - 1));
        setFollowing(false);
      } else {
        await setDoc(doc(db, "users", currentUserId, "following", targetUserId), { followedAt: new Date().toISOString() });
        await setDoc(doc(db, "users", targetUserId, "followers", currentUserId), { followedAt: new Date().toISOString() });
        await updateDoc(doc(db, "users", targetUserId), { followers: increment(1) });
        await updateDoc(doc(db, "users", currentUserId), { following: increment(1) });
        setFollowerCount(c => c + 1);
        setFollowing(true);
      }
    } catch {}
    setLoading(false);
  }

  if (currentUserId === targetUserId) return null;

  return (
    <button onClick={toggleFollow} disabled={loading} style={{ width: "100%", padding: "10px", borderRadius: 50, background: following ? "transparent" : "#1db954", border: following ? "1px solid #333" : "none", color: following ? "#fff" : "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 8 }}>
      {loading ? "..." : following ? "✓ Following" : "+ Follow"}
    </button>
  );
}
function PublicProfile({ artistId, onBack, onPlaySong, currentSong, db, currentUserId }) {
  const [profile, setProfile] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        // Try artists collection first, then users
        let profileData = null;
        const artistDoc = await getDoc(doc(db, "artists", artistId));
        if (artistDoc.exists()) {
          profileData = { id: artistDoc.id, ...artistDoc.data() };
        } else {
          const userDoc = await getDoc(doc(db, "users", artistId));
          if (userDoc.exists()) profileData = { id: userDoc.id, ...userDoc.data() };
        }
        setProfile(profileData);

        // Load their songs
        const q = query(collection(db, "songs"), where("uid", "==", artistId), orderBy("createdAt", "desc"), limit(20));
        const snap = await getDocs(q);
        setSongs(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.hidden));
      } catch {}
      setLoading(false);
    }
    load();
  }, [artistId]);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  if (!profile) return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#555" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>👤</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#fff" }}>Profile not found</div>
    </div>
  );

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* Back button */}
      <button onClick={onBack} style={{ position: "fixed", top: 16, left: 16, zIndex: 200, background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
      </button>

      {/* Cover Banner */}
      <div style={{ height: 130, background: profile.coverPhoto ? `url(${profile.coverPhoto}) center/cover no-repeat` : "linear-gradient(135deg, #0d2818, #1a4a2a)", position: "relative" }} />

      {/* Profile Info */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 16px 16px", marginTop: -40 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", border: "3px solid #000", background: "#111", overflow: "hidden", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {profile.photoURL ? <img src={profile.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 36, height: 36, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
        </div>

        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 2 }}>{profile.displayName || profile.name || "Artist"}</div>
        {profile.premium && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg, #1db954, #0a8f3c)", borderRadius: 50, padding: "3px 10px", marginBottom: 6 }}>
            <span style={{ fontSize: 11 }}>⭐</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>Premium</span>
          </div>
        )}
        {profile.verified && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#1d9bf0", borderRadius: 50, padding: "3px 10px", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>✓ Verified Artist</span>
          </div>
        )}
        {profile.bio && <div style={{ fontSize: 13, color: "#aaa", textAlign: "center", marginBottom: 10, lineHeight: 1.5 }}>{profile.bio}</div>}

        <div style={{ fontSize: 13, color: "#aaa", marginBottom: 16 }}>
          <span style={{ color: "#fff", fontWeight: 700 }}>{profile.followers || 0}</span> Followers &nbsp;·&nbsp;
          <span style={{ color: "#fff", fontWeight: 700 }}>{profile.following || 0}</span> Following
        </div>

<FollowButton currentUserId={currentUserId} targetUserId={artistId} db={db} />
      </div>

      {/* Their Songs */}
      <div style={{ padding: "0 16px" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 14 }}>🎵 Songs</div>
        {songs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#555", fontSize: 13 }}>No songs uploaded yet</div>
        ) : songs.map((s, i) => (
          <div key={s.id} onClick={() => onPlaySong({ trackId: s.id, trackName: s.title, artistName: s.artistName, artworkUrl100: s.coverUrl || "", isOriginal: true, audioUrl: s.audioUrl }, songs)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
            <div style={{ fontSize: 12, color: "#555", width: 20, textAlign: "center" }}>{i + 1}</div>
            <div style={{ width: 44, height: 44, borderRadius: 8, background: "#111", overflow: "hidden", flexShrink: 0 }}>
              {s.coverUrl ? <img src={s.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎵</div>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: currentSong?.trackId === s.id ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
              <div style={{ fontSize: 12, color: "#555" }}>{s.plays || 0} plays</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function ProfileSongs({ uid, db, currentSong, onPlay }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMySongs() {
      try {
        const q = query(collection(db, "songs"), where("uid", "==", uid), orderBy("createdAt", "desc"), limit(20));
        const snap = await getDocs(q);
        setSongs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {}
      setLoading(false);
    }
    fetchMySongs();
  }, [uid]);

  if (loading) return <div style={{ textAlign: "center", padding: 24 }}><div style={{ width: 24, height: 24, border: "2px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} /></div>;

  if (songs.length === 0) return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: "#555" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🎵</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>No uploads yet</div>
      <div style={{ fontSize: 13 }}>Songs you upload to the Feed will appear here</div>
    </div>
  );

  return (
    <div>
      {songs.map((s, i) => (
        <div key={s.id} onClick={() => onPlay(s, songs)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
          <div style={{ fontSize: 12, color: "#555", width: 20, textAlign: "center" }}>{i + 1}</div>
          <div style={{ width: 44, height: 44, borderRadius: 8, background: "#111", overflow: "hidden", flexShrink: 0 }}>
            {s.coverUrl ? <img src={s.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎵</div>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: currentSong?.id === s.id ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
            <div style={{ fontSize: 12, color: "#555" }}>{s.genre || "Music"} · {s.plays || 0} plays</div>
          </div>
        </div>
      ))}
    </div>
  );
}
function AdminDashboard({ onClose, db }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard"); // dashboard, users, artists
const [artists, setArtists] = useState([]);
const [premiumRequests, setPremiumRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadStats(); loadPremiumRequests(); }, []);

  async function loadStats() {
    setLoading(true);
    try {
      const [usersSnap, songsSnap, commSnap, artistsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "songs")),
        getDocs(collection(db, "communities")),
        getDocs(collection(db, "artists")),
      ]);
      const songs = songsSnap.docs.map(d => d.data());
      const totalPlays = songs.reduce((a, s) => a + (s.plays || 0), 0);
      const totalLikes = songs.reduce((a, s) => a + (s.likes || 0), 0);
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const newUsersThisWeek = allUsers.filter(u => u.createdAt && new Date(u.createdAt).getTime() > oneWeekAgo).length;
      setStats({ totalUsers: usersSnap.size, totalSongs: songsSnap.size, totalCommunities: commSnap.size, totalArtists: artistsSnap.size, totalPlays, totalLikes, newUsersThisWeek });
      setUsers(allUsers.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
      const allArtists = artistsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
setArtists(allArtists.sort((a, b) => (b.followers || 0) - (a.followers || 0)));
   } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadPremiumRequests() {
    try {
      const snap = await getDocs(collection(db, "premiumRequests"));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPremiumRequests(all.filter(r => r.status === "pending").sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt)));
    } catch {}
  }

  async function approvePremium(request) {
    setActionLoading(true);
    try {
      await setDoc(doc(db, "users", request.uid), { premium: true, premiumSince: new Date().toISOString() }, { merge: true });
      await updateDoc(doc(db, "premiumRequests", request.id), { status: "approved", approvedAt: new Date().toISOString() });
      await addDoc(collection(db, "users", request.uid, "notifications"), {
        type: "premium",
        message: "🎉 Your MyMusic Premium has been activated! Enjoy ad-free listening and unlimited uploads.",
        read: false,
        createdAt: new Date().toISOString(),
      });
      setPremiumRequests(prev => prev.filter(r => r.id !== request.id));
      alert("✅ Premium activated for " + request.displayName);
    } catch {
      alert("Failed to approve. Try again.");
    }
    setActionLoading(false);
  }

  async function rejectPremium(request) {
    setActionLoading(true);
    try {
      await updateDoc(doc(db, "premiumRequests", request.id), { status: "rejected", rejectedAt: new Date().toISOString() });
      setPremiumRequests(prev => prev.filter(r => r.id !== request.id));
    } catch {}
    setActionLoading(false);
  }
  

  async function banUser(userId, banned) {
    setActionLoading(true);
    try {
      await updateDoc(doc(db, "users", userId), { banned: !banned });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, banned: !banned } : u));
      setSelectedUser(prev => prev ? { ...prev, banned: !banned } : null);
      alert(!banned ? "User banned" : "User unbanned");
    } catch { alert("Failed"); }
    setActionLoading(false);
  }

  async function verifyArtist(userId, verified) {
    setActionLoading(true);
    try {
      // Update artists collection
      await setDoc(doc(db, "artists", userId), { verified: !verified }, { merge: true });
      // Also update users collection
      await setDoc(doc(db, "users", userId), { verified: !verified }, { merge: true });
      setArtists(prev => prev.map(a => a.id === userId ? { ...a, verified: !verified } : a));
      alert(!verified ? "Artist verified ✓" : "Verification removed");
    } catch { alert("Failed"); }
    setActionLoading(false);
  }

  async function sendNotificationToAll(message) {
    if (!message.trim()) return;
    setActionLoading(true);
    try {
      const batch = users.slice(0, 50);
      for (const u of batch) {
        await addDoc(collection(db, "users", u.id, "notifications"), {
          type: "admin",
          message,
          read: false,
          createdAt: new Date().toISOString(),
        });
      }
      alert(`Notification sent to ${batch.length} users!`);
    } catch { alert("Failed"); }
    setActionLoading(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 502, overflowY: "auto", fontFamily: "'DM Sans',sans-serif", color: "#fff" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ padding: "16px", borderBottom: "1px solid #111", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, background: "#000", zIndex: 10 }}>
        <button onClick={view === "users" || view === "artists" ? () => setView("dashboard") : onClose} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div>
         <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>{view === "users" ? "All Users" : view === "artists" ? "All Artists" : "Admin Dashboard"}</div>
<div style={{ fontSize: 11, color: "#555" }}>{view === "users" ? `${users.length} total users` : view === "artists" ? `${artists.length} artists` : "Real-time analytics"}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.2)", borderRadius: 50, padding: "4px 10px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1db954" }} />
          <span style={{ fontSize: 11, color: "#1db954", fontWeight: 600 }}>LIVE</span>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : view === "dashboard" ? (
        <div style={{ padding: 16 }}>
          {/* Stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Total Users", value: stats?.totalUsers || 0, icon: "👥", color: "#1d9bf0", action: () => setView("users") },
              { label: "New This Week", value: stats?.newUsersThisWeek || 0, icon: "🆕", color: "#1db954", action: null },
              { label: "Total Songs", value: stats?.totalSongs || 0, icon: "🎵", color: "#8b5cf6", action: null },
              { label: "Total Artists", value: stats?.totalArtists || 0, icon: "🎤", color: "#f59e0b", action: () => setView("artists") },
              { label: "Total Plays", value: (stats?.totalPlays || 0).toLocaleString(), icon: "▶", color: "#1db954", action: null },
              { label: "Total Likes", value: (stats?.totalLikes || 0).toLocaleString(), icon: "❤️", color: "#e91429", action: null },
              { label: "Communities", value: stats?.totalCommunities || 0, icon: "💬", color: "#ec4899", action: null },
            ].map(stat => (
              <div key={stat.label} onClick={stat.action || undefined} style={{ background: "#111", borderRadius: 14, padding: 16, border: stat.action ? "1px solid #1d9bf0" : "1px solid #1a1a1a", cursor: stat.action ? "pointer" : "default" }}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>{stat.icon}</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: stat.action ? "#1d9bf0" : "#555", marginTop: 4 }}>{stat.label} {stat.action ? "→" : ""}</div>
              </div>
            ))}
          </div>

         {/* Premium Requests */}
          {premiumRequests.length > 0 && (
            <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #f59e0b", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, color: "#f59e0b" }}>⭐ Premium Requests ({premiumRequests.length})</div>
                <button onClick={loadPremiumRequests} style={{ background: "none", border: "none", color: "#1db954", fontSize: 12, cursor: "pointer" }}>Refresh</button>
              </div>
              {premiumRequests.map(req => (
                <div key={req.id} style={{ background: "#0a0a0a", borderRadius: 10, padding: 14, marginBottom: 10, border: "1px solid #1a1a1a" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{req.displayName || req.email}</div>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>{req.email} · ₹{req.amount} · {new Date(req.requestedAt).toLocaleString()}</div>
                  <div style={{ background: "#111", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 12, color: "#1db954", fontFamily: "monospace" }}>
                    Ref: {req.txnId}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => approvePremium(req)} disabled={actionLoading} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#1db954", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ✓ Approve
                    </button>
                    <button onClick={() => rejectPremium(req)} disabled={actionLoading} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #2a2a2a", background: "transparent", color: "#555", fontSize: 12, cursor: "pointer" }}>
                      ✕ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reports section */}
         {/* Reports section */}
<ReportsPanel db={db} />
          {/* Send notification */}
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 16 }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 12 }}>📢 Send Notification to All Users</div>
            <textarea id="notif-msg" placeholder="Write your message..." rows={3}
              style={{ width: "100%", padding: "12px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none", resize: "none", marginBottom: 10 }} />
            <button onClick={() => { const msg = document.getElementById("notif-msg").value; sendNotificationToAll(msg); }}
              disabled={actionLoading} style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {actionLoading ? "Sending..." : "Send to All Users"}
            </button>
          </div>
        </div>
      ) : view === "artists" ? (
  /* Artists list */
  <div style={{ padding: 16 }}>
    {artists.length === 0 ? (
      <div style={{ textAlign: "center", padding: "48px 0", color: "#555" }}>No artists yet</div>
    ) : artists.map(a => (
      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #0a0a0a" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#222", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {a.photoUrl ? <img src={a.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{a.artistName || "Unknown"}</span>
            {a.verified && <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#1d9bf0", display: "flex", alignItems: "center", justifyContent: "center" }}><svg viewBox="0 0 24 24" style={{ width: 9, height: 9, fill: "none", stroke: "#fff", strokeWidth: 3 }}><polyline points="20 6 9 17 4 12"/></svg></div>}
          </div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>{a.genre || "No genre"} · {a.followers || 0} followers</div>
        </div>
        <button onClick={async () => {
          await updateDoc(doc(db, "artists", a.id), { verified: !a.verified });
          setArtists(prev => prev.map(x => x.id === a.id ? { ...x, verified: !x.verified } : x));
        }} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: a.verified ? "#555" : "#1d9bf0", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          {a.verified ? "Unverify" : "Verify ✓"}
        </button>
      </div>
    ))}
  </div>
) : (
  /* Users list */
  <div style={{ padding: 16 }}>
          {users.map(u => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}
              onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#222", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: u.banned ? "2px solid #e91429" : "none" }}>
                {u.photoURL ? <img src={u.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.displayName || u.email?.split("@")[0] || "User"}</span>
                  {u.banned && <span style={{ fontSize: 10, color: "#e91429", fontWeight: 700 }}>BANNED</span>}
                  {u.foundingMember && <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>⭐</span>}
                </div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>{u.email}</div>
              </div>
              <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#333", strokeWidth: 2, transform: selectedUser?.id === u.id ? "rotate(90deg)" : "none" }}><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          ))}

          {/* User detail panel */}
          {selectedUser && (
            <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, border: "1px solid #2a2a2a", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#222", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {selectedUser.photoURL ? <img src={selectedUser.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, fill: "none", stroke: "#555", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedUser.displayName || selectedUser.email?.split("@")[0]}</div>
                  <div style={{ fontSize: 12, color: "#555" }}>{selectedUser.email}</div>
                  <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>Joined: {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : "Unknown"}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <button onClick={() => banUser(selectedUser.id, selectedUser.banned)} disabled={actionLoading}
                  style={{ padding: 12, borderRadius: 10, border: "none", background: selectedUser.banned ? "#1db954" : "#e91429", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {selectedUser.banned ? "Unban User" : "Ban User"}
                </button>
                <button onClick={() => verifyArtist(selectedUser.id, false)} disabled={actionLoading}
                  style={{ padding: 12, borderRadius: 10, border: "none", background: "#1d9bf0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Verify Artist ✓
                </button>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ width: "100%", marginTop: 10, padding: 12, borderRadius: 10, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 13, cursor: "pointer" }}>
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function BottomNav({ page, setPage, setProfileTab, currentSong, setShowFullPlayer, setFloatingPlayerVisible, setFloatingPlayerExpanded, onClearSearch }) {
  const { getTranslation } = require("./translations");
  const tr = getTranslation(localStorage.getItem("app_language") || "en");
  const tabs = [
  { id: "home", label: tr.home || "Home", icon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={page === "home" ? "#fff" : "#555"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12L12 3l9 9"/><path d="M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9"/>
    </svg>
  )},
  { id: "search", label: tr.search || "Search", icon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={page === "search" ? "#fff" : "#555"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7"/><path d="M16.5 16.5L21 21"/>
    </svg>
  )},
  { id: "charts", label: tr.charts || "Charts", icon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={page === "charts" ? "#fff" : "#555"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="12" width="4" height="9" rx="1"/><rect x="10" y="7" width="4" height="14" rx="1"/><rect x="17" y="3" width="4" height="18" rx="1"/>
    </svg>
  )},
  { id: "feed", label: tr.feed || "Feed", icon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={page === "feed" ? "#fff" : "#555"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
      <circle cx="19" cy="8" r="2.5"/><path d="M21.5 18c0-2.5 1.5-4 2.5-4"/>
    </svg>
  )},
  { id: "create", label: "Create", icon: <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: page === "create" ? "#1db954" : "#555", strokeWidth: 1.8 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg> },
    
  { id: "profile", label: tr.profile || "Profile", icon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={page === "profile" ? "#fff" : "#555"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )},
];
  
  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 60, background: "rgba(0,0,0,0.97)", borderTop: "1px solid #1a1a1a", display: "flex", alignItems: "center", zIndex: 501, backdropFilter: "blur(20px)" }}>
      {tabs.map(tab => (
       <div key={tab.id} onClick={() => { window.scrollTo(0, 0); document.body.scrollTop = 0; document.documentElement.scrollTop = 0;if (tab.id !== "search") { onClearSearch && onClearSearch(); } setPage(tab.id); if (tab.id !== "profile") setProfileTab(null); if (currentSong) { setShowFullPlayer(false); setFloatingPlayerVisible(true); setFloatingPlayerExpanded(false); } window.history.pushState({ page: tab.id }, "", window.location.href); }} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "8px 0", position: "relative" }}>
          {tab.icon}
          <span style={{ fontSize: 10, color: page === tab.id ? "#fff" : "#555", fontWeight: page === tab.id ? 600 : 400, letterSpacing: "0.02em" }}>{tab.label}</span>
          {page === tab.id && <div style={{ position: "absolute", bottom: 2, width: 4, height: 4, borderRadius: "50%", background: "#1db954" }} />}
        </div>
      ))}
    </div>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
function App() {
  const isInAppBrowser = /FB|FBAN|FBAV|Instagram|WhatsApp|Messenger/i.test(navigator.userAgent);
  const [language, setLanguage] = useState(() => localStorage.getItem("app_language") || "en");
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(() => {
    const hash = window.location.hash.replace("#", "");
    if (["terms", "privacy", "about"].includes(hash)) return hash;
    return "landing";
  });
 const initialProfileId = new URLSearchParams(window.location.search).get("user");
  const initialUsername = window.location.pathname.startsWith("/@") ? window.location.pathname.slice(2) : null;
  const [checking, setChecking] = useState(true);


  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [page]);
  
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (["terms", "privacy", "about"].includes(hash)) {
        setPage(hash);
      }
    };
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);
  useEffect(() => {
    onAuthStateChanged(auth, u => {
      setUser(u);
      setChecking(false);
      if (u) {
        setPage("home");
        if (initialProfileId) {
          setTimeout(() => {
            const event = new CustomEvent("openProfile", { detail: initialProfileId });
            window.dispatchEvent(event);
          }, 1000);
        }

        // Always ensure user document exists
        const userRef = doc(db, "users", u.uid);
        getDoc(userRef).then(snap => {
          if (!snap.exists()) {
            setDoc(userRef, {
              uid: u.uid,
             email: u.email || "",
              displayName: u.displayName || "",
              photoURL: u.photoURL || "",
              username: (u.displayName || u.email?.split("@")[0] || "user").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, ""),
              createdAt: new Date().toISOString(),
            }, { merge: true });
          } else {
            setDoc(userRef, {
              lastActive: new Date().toISOString(),
            }, { merge: true });
          }
        });

        // Handle referral on signup
        const ref = new URLSearchParams(window.location.search).get("ref");
        if (ref && ref !== u.uid) {
          const userRef = doc(db, "users", u.uid);
          getDoc(userRef).then(snap => {
            if (!snap.exists() || !snap.data().foundingMember) {
              // New user — give founding member badge
              setDoc(userRef, {
                foundingMember: true,
                memberNumber: Date.now(),
                referredBy: ref,
                joinedAt: new Date().toISOString(),
              }, { merge: true });
              // Give referrer badge too
              setDoc(doc(db, "users", ref), {
                foundingMember: true,
                memberNumber: Date.now() - 1,
              }, { merge: true });
            }
          });
        }
      }
    });
  }, []);

  if (isInAppBrowser) return (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", fontFamily: "'DM Sans',sans-serif", color: "#fff" }}>
      <div style={{ fontSize: 56, marginBottom: 20 }}>🌐</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Open in Browser</div>
      <p style={{ fontSize: 14, color: "#666", lineHeight: 1.8, marginBottom: 28, maxWidth: 280 }}>For the best experience, please open MyMusic in Chrome or Safari.</p>
      <button onClick={() => window.open("https://mymusicstream.online", "_blank")} style={{ padding: "14px 28px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Open in Browser</button>
    </div>
  );

  if (checking) return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@800&display=swap'); *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } body { background: #000; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{ width: 64, height: 64, background: "#1db954", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 24 24" style={{ width: 30, height: 30, fill: "#000" }}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        </div>
        <div style={{ fontFamily: "Syne,sans-serif", fontSize: 24, fontWeight: 800, color: "#fff" }}>MyMusic</div>
        <div style={{ width: 20, height: 20, border: "2px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      </div>
    </>
  );

  if (!user) {
    if (page === "login") return <Login onLogin={() => setPage("home")} onSignup={() => setPage("signup")} />;
    if (page === "signup") return <Signup onSignup={() => setPage("home")} onLogin={() => setPage("login")} />;
    if (page === "terms") return (
      <div style={{ padding: "40px 24px", maxWidth: 700, margin: "0 auto", fontFamily: "'DM Sans',sans-serif", color: "#000" }}>
        <a href="/" style={{ fontSize: 13, color: "#999", textDecoration: "none", display: "block", marginBottom: 24 }}>← Back to MyMusic</a>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Terms of Service</h1>
        <p style={{ fontSize: 12, color: "#999", marginBottom: 32 }}>Last updated: June 2026 · mymusicstream.online</p>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>By using MyMusic, you agree to these terms. MyMusic is a free music streaming and social platform available at mymusicstream.online.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>1. Usage</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>You must be 13 or older to use MyMusic. You are responsible for your account and content you upload.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>2. Content</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>You may only upload original music you own or have rights to. Copyrighted content without permission is prohibited.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>3. Privacy</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>We collect basic account information to provide our services. We do not sell your data. See our Privacy Policy for details.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>4. Contact</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444" }}>Email: support@mymusicstream.online</p>
      </div>
    );
    if (page === "privacy") return (
      <div style={{ padding: "40px 24px", maxWidth: 700, margin: "0 auto", fontFamily: "'DM Sans',sans-serif", color: "#000" }}>
        <a href="/" style={{ fontSize: 13, color: "#999", textDecoration: "none", display: "block", marginBottom: 24 }}>← Back to MyMusic</a>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ fontSize: 12, color: "#999", marginBottom: 32 }}>Last updated: June 2026 · mymusicstream.online</p>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>MyMusic ("we", "our") is committed to protecting your privacy. This policy explains how we collect and use your information.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>1. Information We Collect</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>We collect your name, email address, and profile photo when you sign up via Google or Apple. We also collect music listening history to personalize your experience.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>2. How We Use Your Information</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>We use your information to provide and improve MyMusic services, personalize your music recommendations, and send important account notifications.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>3. Data Sharing</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>We do not sell your personal data. We use Firebase (Google) for authentication and database, and Cloudinary for media storage.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>4. Cookies</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>We use localStorage for storing your preferences and listening history. We use Google Analytics to understand site usage.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>5. Contact</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444" }}>For privacy concerns, contact us at support@mymusicstream.online</p>
      </div>
    );
    if (page === "about") return (
      <div style={{ padding: "40px 24px", maxWidth: 700, margin: "0 auto", fontFamily: "'DM Sans',sans-serif", color: "#000" }}>
        <a href="/" style={{ fontSize: 13, color: "#999", textDecoration: "none", display: "block", marginBottom: 24 }}>← Back to MyMusic</a>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>About MyMusic</h1>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>MyMusic is a free music streaming and social platform built for music lovers and artists worldwide. We are home to the Universal Music Society — the world's first global music membership community.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Our Mission</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444", marginBottom: 16 }}>To connect every music lover and artist on earth through a platform that treats music as identity, community and culture.</p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Contact</h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, color: "#444" }}>Email: support@mymusicstream.online<br />Website: mymusicstream.online</p>
      </div>
    );
    return <LandingPage onGetStarted={() => setPage("login")} />;
  }

  const onboarding = JSON.parse(localStorage.getItem(`onboarding_${user.uid}`) || "{}");
  if (!onboarding.completed) {
    return <Onboarding user={user} onComplete={() => window.location.reload()} />;
  }

  return (
    <Home
      user={user}
      language={language}
      onLanguageChange={lang => { setLanguage(lang); localStorage.setItem("app_language", lang); }}
      initialProfileId={initialProfileId || initialUsername}
      initialIsUsername={!!initialUsername}
    />
  );
}

export default App;